/**
 * `MandateDetail` — audit-record detail panel.
 *
 * Phase 2 (2026-05-31): the panel now renders two shapes by chain class:
 *
 *   - Discovery (`decision === 'observed'`): identification surface +
 *     audit envelope. No principal, no intent — the record is a
 *     "this agent visited" observation, not an authorization.
 *
 *   - Transactional (every other decision): outcome pill colored by
 *     the decision, `mandate_id` pointer, policy version + per-rule
 *     trace, audit envelope. The canonical signed mandate body
 *     (agent_id, intent, principal, signature, key_id) is NOT
 *     persisted server-side per ADR-0029 §4.1 — only the sanitized
 *     summary is in the DB, so the panel surfaces what we have and
 *     names the gap.
 *
 * The panel is its own scroll container: `sticky top-24` with
 * `max-h-[calc(100vh-7rem)]` and `overflow-y-auto`. The bottom
 * (envelope) is reachable without scrolling the records list
 * underneath.
 */

import { useEffect, useRef, useState } from 'react';
import { AlertOctagon, Check, ChevronDown, ChevronRight, Copy, Download, FileJson, ShieldCheck, Sparkles, X } from 'lucide-react';

import type {
  AuditConnectorCall,
  AuditDecision,
  AuditRecord,
  AuditRecordPayload,
  AuditRetentionClass,
  BotSummary,
  RuleResult,
} from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import type { EscalationOriginalOrder } from '../../api/escalations.js';
import { useC } from '../../lib/theme.js';
import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { formatIsoDate, formatMinor } from '../../lib/format.js';
import { Card, Pill, type PillTone, RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { CountryFlag, countryName } from '../../components/atoms/CountryFlag.js';
import { BuyerDetailPanel } from '../audit/BuyerDetailPanel.js';
import { OriginalOrderPanel } from '../audit/OriginalOrderPanel.js';
import { effectiveOutcome, shortPolicyLabel } from './effective-outcome.js';
import { type BundleSplit, exportAuditBundleHTML, exportErrorMessage, exportAuditRecordJSON } from './export.js';
import { BundleDayNote } from './BundleDayNote.js';
import { BundlePartsNote } from './BundlePartsNote.js';
import type { MandateRecord } from './types.js';
import {
  AGENT_ACTIVITY,
  computeSpoofSignal,
  describeIntent,
  displayAgentName,
  extractCompatibleBotName,
  formatIntentAmount,
  friendlyActionLabel,
  friendlyRejectReason,
  inferAgentFromUserAgent,
  recordHeadline,
  RULE_ACTION_TOOLTIP,
  RULE_STATE_LABEL,
  RULE_STATE_TOOLTIP,
  ruleResultState,
  settledTotalForHeadline,
} from './intent-label.js';
import { humanRecordDevice, humanRecordName, identityState } from '@sill/audit';
import { mismatchReason, quotedName, visitListName } from './visit-identity.js';
import { VisitIdentityMark, VisitLogoGlyph } from './VisitIdentityMarks.js';
import { ProviderLogo } from './ProviderLogo.js';
import { MandateDetailV3 } from './MandateDetailV3.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

/** Narrow setView type for navigating to Guardrails with optional pre-fill. */
export type NavToGuardrails = (
  view: 'guardrails',
  opts?: {
    preset_rule_type?: string;
    preset_agent_id?: string;
    preset_action_on_match?: 'reject' | 'escalate';
  },
) => void;

// AGENT_DISPLAY_NAME + displayAgentName live in `./intent-label.ts` so
// the Mandates row card, the MandateDetail panel, and `recordHeadline()`
// (the search index) all resolve agent_id → friendly-name from the
// same map. Imported below.

function displayAgent(agent_id: string | undefined): string {
  return displayAgentName(agent_id);
}

export interface DecisionPresentation {
  tone: PillTone;
  label: string;
  /** "DISCOVERY" or "TRANSACTIONAL" — drives section layout. */
  chain_class: 'discovery' | 'transactional';
}

export function presentDecision(decision: AuditDecision): DecisionPresentation {
  switch (decision) {
    case 'observed':
      return { tone: 'sky', label: 'OBSERVED', chain_class: 'discovery' };
    case 'approved':
      return { tone: 'lime', label: 'APPROVED', chain_class: 'transactional' };
    case 'rejected':
      return { tone: 'coral', label: 'REJECTED', chain_class: 'transactional' };
    case 'escalated_approved':
      return { tone: 'amber', label: 'ESCALATED · APPROVED', chain_class: 'transactional' };
    case 'escalated_rejected':
      return { tone: 'amber', label: 'ESCALATED · REJECTED', chain_class: 'transactional' };
    case 'verification_rejected':
      return { tone: 'coral', label: 'VERIFICATION REJECTED', chain_class: 'transactional' };
    case 'rejected_post_verify':
      return { tone: 'coral', label: 'REJECTED · POST-VERIFY', chain_class: 'transactional' };
    // ADR-0157 (Bucket B Slice 2) — RAIL-NEUTRAL settlement audit records
    // bound into the signed audit scope AFTER the ledger row insert. Both
    // ride the transactional per-site chain (chain_class='transactional').
    // Per §4 semantic palette: mint=confirmed (positive rail settlement),
    // coral=reject/reversal (refund settlement).
    case 'settled_succeeded':
      return { tone: 'mint', label: 'SETTLED', chain_class: 'transactional' };
    case 'settled_refunded':
      return { tone: 'coral', label: 'REFUNDED', chain_class: 'transactional' };
    // ADR-0174 §2 (Slice 1) — WooCommerce PAYMENT-LINK HANDOFF outcome.
    // Categorically NOT a settlement (no money moved on the Sill side);
    // the buyer completes payment on the merchant's own site. Per
    // CLAUDE.md §4 palette: sky=pending is the semantic fit for
    // "pending payment on the merchant's site". Placeholder label
    // pending Slice 3's marketing-owned copy pass.
    case 'handoff_pending':
      return { tone: 'sky', label: 'HANDOFF', chain_class: 'transactional' };
  }
}

export interface MandateDetailProps {
  record: AuditRecord;
  /**
   * ADR-0059 §6 — inspectable request/response content from the
   * single-record `/v1/audit/:record_id` projection. Absent on the
   * list view; present on the detail view. The dashboard re-hashes
   * the canonical_json client-side and compares to
   * `record.request_hash` / `record.response_hash` so a merchant can
   * verify the signed envelope still binds to the stored content.
   */
  payload?: AuditRecordPayload;
  /**
   * Layout override for tests — bypasses the localStorage preference.
   * Production callers leave this undefined; the founder-facing toggle
   * persists the choice under `sill_mandate_detail_layout`.
   */
  layout_override?: MandateDetailLayout;
  /**
   * Pass-through for `BuyerDetailPanel.reveal` (ADR-0120 §4 must-hold
   * 3). Default `'auto'` preserves the Mandates view behavior EXACTLY
   * (decrypt fetch fires on mount for `can_decrypt` roles); the
   * shadow-log "View mandate" pivot (`ShadowMandatePanel` in
   * `views/guardrails/Guardrails.tsx`) passes `'on_click'` so policy
   * review does not auto-decrypt buyer PII. Forwarded to BOTH the
   * Classic and v2 layout BuyerDetailPanel mounts.
   */
  buyerReveal?: 'auto' | 'on_click';
  /**
   * Container scroll/positioning mode. Default `'sticky'` keeps the
   * original Mandates-view chrome (Card pinned with `sticky top-24`
   * and `max-h-[calc(100vh-7rem)]` + internal `overflow-y-auto`).
   * `'flat'` strips ALL of those — sticky positioning, max-height,
   * and the inner overflow region — so the panel flows at its natural
   * height inside an outer scroll container that owns the single
   * scrollbar (the dry-run "View mandate" drawer; ADR-0120 §2.1(c)).
   * The drawer body has its own `overflow-y-auto`; layering a second
   * one here produced the triple-nested scrollbar the founder flagged.
   */
  containerLayout?: 'sticky' | 'flat';
  /**
   * The registered domain for `record.site_id`, when the caller already
   * has the site in context (every real mount does — `Mandates.tsx` and
   * `Guardrails.tsx` both hold `activeSite`). Optional so callers without
   * a resolved site fall back to the raw `site_id`. Not sourced from the
   * wire: `AuditRecord` carries only `site_id` (packages/types/src/audit.ts)
   * — the API never joins `sites.domain` onto the audit projection.
   */
  siteDomain?: string;
  /**
   * ADR-0204 Slice 3 — structured bot classification from the single-record
   * detail fetch. Absent until the background classifier runs and the detail
   * is fetched. Merged into `record` before passing to layout components so
   * DiscoveryRows can read `record.ai_summary` without threading it separately.
   */
  ai_summary?: BotSummary;
  /**
   * ADR-0204 follow-up — content-addressed agent_key from the single-record
   * detail fetch (SHA-256 hex of lower(trim(user_agent))). Threaded so
   * BotClassificationRow can call `POST /v1/bots/:agent_key/dismiss-action`
   * without recomputing the hash. Merged into `record.agent_key`.
   */
  agent_key?: string;
  /**
   * ADR-0204 follow-up — true when the current account has dismissed the
   * classifier's `action` recommendation for this bot's agent_key. Merged
   * into `record.bot_action_dismissed`.
   */
  bot_action_dismissed?: boolean;
  /**
   * ADR-0204 follow-up — navigate to the top-level Guardrails/policy view.
   * Threaded down so `BotClassificationRow`'s "Open policy editor" /
   * "Set up escalation rule" one-click buttons can jump the merchant
   * straight to the surface that owns rule editing (the dashboard's
   * `guardrails` view — the policy editor per glossary §10). Optional:
   * dry-run drawers and other embedders that lack routing context omit
   * it, and the row falls back to a text-only recommendation.
   */
  setView?: NavToGuardrails;
}

/**
 * The opt-in v2 layout reorders the panel around "what happened" (the
 * outcome pill + headline + request/response capture) instead of "what's
 * the record" (record_id first, payload near the bottom). The Classic
 * layout is the legacy default; the toggle at the top of the panel
 * persists the founder's choice in localStorage so they can preview the
 * new layout across records and revert with one click.
 */
export type MandateDetailLayout = 'classic' | 'v2' | 'v3';

const LAYOUT_STORAGE_KEY = 'sill_mandate_detail_layout';

function readStoredLayout(): MandateDetailLayout {
  if (typeof window === 'undefined') {
    return 'classic';
  }
  try {
    const raw: unknown = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw === 'classic' || raw === 'v2' || raw === 'v3') {
      return raw;
    }
  } catch {
    // Storage may be disabled (incognito, file://, locked-down profile).
    // Treat as no preference and fall through to the default.
  }
  return 'classic';
}

function writeStoredLayout(layout: MandateDetailLayout): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
  } catch {
    // See readStoredLayout.
  }
}

/**
 * `MandateDetail` — entry point. Reads the persisted layout preference
 * and dispatches to either the Classic (legacy) or v2 (opt-in) renderer.
 * The toggle floats above the rendered Card so the Classic JSX inside
 * stays byte-identical to its pre-toggle shape.
 */
export function MandateDetail(props: MandateDetailProps): JSX.Element {
  // ADR-0003 exactOptionalPropertyTypes — payload is `?:` on the props
  // and on both inner components, so spread it only when defined to
  // avoid the explicit-undefined assignability error.
  const payloadProp = props.payload !== undefined ? { payload: props.payload } : {};
  // ADR-0120 §4 must-hold 3 — pass-through for the buyer-reveal gate.
  // Default `'auto'` keeps the Mandates view path unchanged; the
  // shadow-log pivot supplies `'on_click'`.
  const buyerReveal: 'auto' | 'on_click' = props.buyerReveal ?? 'auto';
  // `feat/dry-run-ux-polish` (2) — pass-through for the container
  // scroll mode. Default `'sticky'` keeps the Mandates-view chrome
  // unchanged; the drawer caller supplies `'flat'` so the panel flows
  // inside the drawer's single `overflow-y-auto` body.
  const containerLayout: 'sticky' | 'flat' = props.containerLayout ?? 'sticky';
  const siteDomainProp = props.siteDomain !== undefined ? { siteDomain: props.siteDomain } : {};
  // ADR-0204 Slice 3 / follow-up — merge single-record-only fields into
  // `record` so DiscoveryRows can read `record.ai_summary` /
  // `record.agent_key` / `record.bot_action_dismissed` without threading
  // each one separately. The list record never carries these; this
  // enrichment only fires when the single-record route has returned them.
  const record: AuditRecord = {
    ...props.record,
    ...(props.ai_summary !== undefined ? { ai_summary: props.ai_summary } : {}),
    ...(props.agent_key !== undefined ? { agent_key: props.agent_key } : {}),
    ...(props.bot_action_dismissed !== undefined
      ? { bot_action_dismissed: props.bot_action_dismissed }
      : {}),
  };
  const setViewProp = props.setView !== undefined ? { setView: props.setView } : {};

  // Layout toggle: persisted in localStorage. `layout_override` lets tests
  // pin a specific layout without touching storage. Skip classic — it's not
  // rendered at this entry point; only v2 and v3 are live.
  const initialLayout: MandateDetailLayout = (() => {
    if (props.layout_override !== undefined) return props.layout_override;
    const stored = readStoredLayout();
    return stored === 'v3' ? 'v3' : 'v2';
  })();
  const [layout, setLayout] = useState<MandateDetailLayout>(initialLayout);

  // When a layout_override is supplied (e.g. test harness), sync without
  // touching localStorage so test state stays isolated.
  const overrideRef = useRef(props.layout_override);
  useEffect(() => {
    if (overrideRef.current !== undefined) {
      setLayout(overrideRef.current);
    }
  }, []);

  const onLayoutChange = (next: MandateDetailLayout): void => {
    setLayout(next);
    writeStoredLayout(next);
  };

  const C = useC();

  if (layout === 'v3') {
    return (
      <>
        <LayoutToggle C={C} value={layout} onChange={onLayoutChange} />
        <MandateDetailV3
          record={record}
          buyerReveal={buyerReveal}
          containerLayout={containerLayout}
          {...payloadProp}
          {...siteDomainProp}
          {...setViewProp}
        />
      </>
    );
  }
  return (
    <>
      <LayoutToggle C={C} value={layout} onChange={onLayoutChange} />
      <MandateDetailV2
        record={record}
        buyerReveal={buyerReveal}
        containerLayout={containerLayout}
        {...payloadProp}
        {...siteDomainProp}
        {...setViewProp}
      />
    </>
  );
}

/**
 * Segmented `CLASSIC` | `NEW (BETA)` control. Mono uppercase ~10px with
 * letter-spacing to match the kicker typography across the panel; the
 * active segment uses the lime accent.
 */
function LayoutToggle({
  C,
  value,
  onChange,
}: {
  C: ReturnType<typeof useC>;
  value: MandateDetailLayout;
  onChange: (next: MandateDetailLayout) => void;
}): JSX.Element {
  const segment = (
    key: MandateDetailLayout,
    label: string,
  ): JSX.Element => {
    const active = value === key;
    return (
      <button
        type="button"
        onClick={() => onChange(key)}
        aria-pressed={active}
        aria-label={`Layout: ${label}`}
        style={{
          background: active ? C.lime : 'transparent',
          color: active ? C.ink : C.muted,
          border: 'none',
          padding: '3px 8px',
          fontFamily: monoFont,
          fontSize: 9,
          letterSpacing: 1.5,
          fontWeight: active ? 700 : 500,
          cursor: 'pointer',
          borderRadius: 2,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div
      className="flex items-center justify-end mb-2"
      style={{ gap: 0 }}
    >
      <div
        role="group"
        aria-label="Audit-record layout"
        className="inline-flex items-center"
        style={{
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: 2,
          gap: 2,
        }}
      >
        {segment('v2', 'Current')}
        {segment('v3', 'Agent View')}
      </div>
    </div>
  );
}

function MandateDetailClassic({
  record,
  payload,
  buyerReveal,
  containerLayout,
  siteDomain,
}: {
  record: AuditRecord;
  payload?: AuditRecordPayload;
  buyerReveal: 'auto' | 'on_click';
  containerLayout: 'sticky' | 'flat';
  siteDomain?: string;
}): JSX.Element {
  const C = useC();
  const api = useApi();
  const timeZone = useDisplayTimeZone();
  const presented = presentDecision(record.decision);
  const is_discovery = presented.chain_class === 'discovery';
  // ADR-0059 amendment §A.4.3 — render rule trace whenever the record
  // carries one. Pre-amendment connector records had `rules_evaluated:
  // []` and the panel was hidden; post-amendment, connector records
  // carry an advisory verdict and the panel renders with the
  // ADVISORY kicker + micro-copy.
  //
  // ADR-0059 security review MAJOR M3 — the signed `RuleResult.surface
  // === 'tool_call_advisory'` flag is the structural signal that a
  // verdict is observe-only. Use the signed-field signal as the
  // primary discriminator; the `is_discovery + is_connector_record`
  // heuristic is the back-compat fallback for pre-M3 records.
  const has_rules_trace = record.rules_evaluated.length > 0;
  const is_connector_record = record.connector_call !== undefined;
  const rules_carry_advisory_surface = record.rules_evaluated.some(
    (r) => (r as { surface?: string }).surface === 'tool_call_advisory',
  );
  const advisory_mode =
    rules_carry_advisory_surface
    || (is_discovery && is_connector_record && has_rules_trace);
  // A discovery record with a skill_id is an unwired MCP tool-call
  // observation (e.g. `check_availability`, `recommend`), not a pure
  // embed beacon. Surface that distinction in the kicker so the panel
  // header tells the merchant what flavor of observation this is.
  const is_tool_call =
    is_discovery
    && typeof record.skill_id === 'string'
    && record.skill_id.length > 0;

  /** Per-button busy + error state so silent failures (CORS, 404, signed-
   *  out, network) surface to the merchant instead of clicks doing
   *  nothing. Each click resets BOTH so a successful retry clears the
   *  prior error. */
  const [exportingJson, setExportingJson] = useState(false);
  const [exportingHtml, setExportingHtml] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [bundleSplit, setBundleSplit] = useState<BundleSplit | null>(null);

  const onExportJson = async (): Promise<void> => {
    setExportingJson(true);
    setExportError(null);
    try {
      setBundleSplit(await exportAuditRecordJSON(api, record, siteDomain));
    } catch (err) {
      setExportError(
        exportErrorMessage('Export', err),
      );
    } finally {
      setExportingJson(false);
    }
  };

  const onExportHtml = async (): Promise<void> => {
    setExportingHtml(true);
    setExportError(null);
    try {
      setBundleSplit(await exportAuditBundleHTML(api, record, siteDomain));
    } catch (err) {
      setExportError(
        exportErrorMessage('Bundle export', err),
      );
    } finally {
      setExportingHtml(false);
    }
  };

  // `feat/dry-run-ux-polish` (2) — when the panel is embedded in the
  // dry-run drawer, the outer drawer body owns the single
  // `overflow-y-auto` scroll. Strip the sticky positioning, the
  // max-height, AND the inner overflow region so the panel flows at
  // natural height inside the drawer.
  const cardClassName =
    containerLayout === 'flat' ? 'p-5' : 'p-5 sticky top-24 overflow-y-auto';
  const cardStyle =
    containerLayout === 'flat' ? undefined : { maxHeight: 'calc(100vh - 7rem)' };
  return (
    <Card className={cardClassName} {...(cardStyle !== undefined ? { style: cardStyle } : {})}>
      <div className="flex items-start justify-between gap-2 mb-4 flex-wrap">
        <div className="min-w-0">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 4,
            }}
          >
            {is_tool_call
              ? 'AUDIT RECORD · TOOL CALL'
              : is_discovery
                ? 'AUDIT RECORD · DISCOVERY'
                : 'AUDIT RECORD · TRANSACTIONAL'}
          </div>
          <div
            style={{
              fontFamily: serifFont,
              fontSize: 22,
              color: C.text,
              letterSpacing: -0.5,
              lineHeight: 1.1,
              wordBreak: 'break-all',
            }}
          >
            {record.record_id}
          </div>
        </div>
        <Pill tone={presented.tone}>{presented.label}</Pill>
      </div>

      <dl className="space-y-3 mb-5">
        <Row
          C={C}
          label="Site"
          tooltip="The site this visit hit. Each site you register in Sill gets its own record scope — only your account can see this site's audit records."
        >
          {siteDomain !== undefined ? (
            <div>
              <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
                {siteDomain}
              </span>
              <div
                style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 2, wordBreak: 'break-all' }}
              >
                {record.site_id}
              </div>
            </div>
          ) : (
            <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, wordBreak: 'break-all' }}>
              {record.site_id}
            </span>
          )}
        </Row>

        {is_discovery ? (
          <DiscoveryRows C={C} record={record} />
        ) : (
          <TransactionalRows C={C} record={record} />
        )}

        <Row C={C} label="Evaluated at">
          <div style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>
            {formatIsoDate(record.evaluated_at, timeZone)}
          </div>
          <RelativeTime
            iso={record.evaluated_at}
            className="block"
            style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginTop: 2 }}
          />
        </Row>
        <Row C={C} label="Policy version">
          <span
            style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}
            title={record.policy_version} /* title-ok: full value behind the shortened label */
          >
            {friendlyPolicyVersion(record.policy_version)}
          </span>
        </Row>
        <Row C={C} label="Retention">
          <Pill tone="neutral">{record.retention_class}</Pill>
        </Row>
      </dl>

      {advisory_mode ? (
        <AdvisoryVerdictPill C={C} rules={record.rules_evaluated} />
      ) : null}

      {has_rules_trace ? (
        <RulesTrace C={C} record={record} advisory={advisory_mode} />
      ) : null}

      {record.connector_call !== undefined ? (
        <ConnectorCallPanel C={C} call={record.connector_call} />
      ) : null}

      <SettlementPanel
        C={C}
        settlement={readSettlement(record)}
        requestedDiscountCode={record.intent_summary?.discount?.code}
      />

      <HandoffPanel C={C} handoff={readHandoff(record)} />

      {/* ADR-0099 follow-up — for a refund record the order facts + buyer
          live on the server-resolved ORIGINAL order, not the refund
          mandate. */}
      {readSettlement(record)?.kind === 'refund' ? (
        <OriginalOrderPanel C={C} original={readOriginalOrder(record) ?? null} />
      ) : null}

      {/* ADR-0065 §10 — buyer-detail panel between intent + connector
       *  summary and the request/response capture. Transactional-only
       *  by design: discovery records never carry a signed buyer
       *  block so the panel would always render State A
       *  (`buyer_absent_at_mandate`); skipping the mount avoids a
       *  guaranteed 404 round-trip on every observed beacon. Suppressed
       *  on refunds: the buyer rides the ORIGINAL order panel above. */}
      {!is_discovery && readSettlement(record)?.kind !== 'refund' ? (
        <BuyerDetailPanel record_id={record.record_id} reveal={buyerReveal} />
      ) : null}

      {payload !== undefined ? (
        <PayloadPanel
          C={C}
          payload={payload}
          /* ADR-0059 §8 — hashes may be absent on pre-widening rows.
           * Pass empty string so the "Verify hash" affordance compares
           * to a hash the merchant can never reproduce (a deliberate
           * UX cue that verification is not available on legacy rows). */
          request_hash={record.request_hash ?? ''}
          response_hash={record.response_hash ?? ''}
        />
      ) : null}

      <div
        className="p-3 mb-5"
        style={{
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 6,
          }}
        >
          AUDIT ENVELOPE
        </div>
        <Anchor C={C} label="envelope_signature" value={record.envelope_signature} />
        <Anchor C={C} label="prev_record_hash" value={record.prev_record_hash} />
        <Anchor C={C} label="merkle_root" value={record.merkle_root} />
      </div>

      {!is_discovery ? (
        <div
          className="p-3 mb-5"
          style={{
            background: 'transparent',
            border: `1px dashed ${C.borderSoft}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          The canonical signed mandate body (intent, principal, agent_id,
          ed25519 signature, key_id) is verified at the edge and intentionally
          NOT persisted server-side. The audit record carries
          only the sanitized summary above. The chain anchors prove the record
          was committed, not the mandate's contents.
        </div>
      ) : null}

      {/* ADR-0024 — HTML export un-retired. The server renders the
          bundle as HTML with the canonical signed JSON embedded in
          a <script type="application/sill+json"> block, so the
          cryptographic standing is identical to the JSON export. */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => void onExportJson()}
          disabled={exportingJson || exportingHtml}
          className="flex items-center justify-center gap-2 px-3 py-2 flex-1"
          style={{
            background: exportingJson ? C.surface2 : C.lime,
            color: exportingJson ? C.muted : C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            cursor: exportingJson || exportingHtml ? 'not-allowed' : 'pointer',
          }}
        >
          <FileJson size={13} /> {exportingJson ? 'Exporting…' : 'Export JSON'}
        </button>
        <button
          type="button"
          onClick={() => void onExportHtml()}
          disabled={exportingJson || exportingHtml}
          className="flex items-center justify-center gap-2 px-3 py-2 flex-1"
          style={{
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            fontFamily: sansFont,
            fontWeight: 500,
            fontSize: 13,
            borderRadius: 2,
            cursor: exportingJson || exportingHtml ? 'not-allowed' : 'pointer',
          }}
        >
          <Download size={13} /> {exportingHtml ? 'Exporting…' : 'Audit bundle (HTML)'}
        </button>
      </div>
      {exportError !== null ? (
        <div
          className="mt-2 p-2 flex items-start gap-2"
          style={{
            background: C.coralDim,
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
          }}
        >
          <AlertOctagon size={12} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.4 }}>
            {exportError}
          </span>
        </div>
      ) : null}
      <BundleDayNote C={C} record={record} />
      <BundlePartsNote C={C} api={api} record={record} siteDomain={siteDomain} split={bundleSplit} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// v2 layout — opt-in. Reorders the panel around "what happened" (outcome
// + headline + the request/response exchange) instead of "what's the
// record" (record_id banner + identity rows + payload near the bottom).
//
// Sections, top to bottom:
//   1. Header  — outcome pill, serif headline, record_id + identity line.
//   2. THE EXCHANGE — the existing <PayloadPanel /> rendered first so the
//      bytes the agent sent + the bytes we returned are the hero.
//   3. DETAILS  — site / principal / connector / policy / retention /
//      evaluated_at. Friendlier labels for policy + retention; connector
//      summary inline so the visible view never shows `arguments_hash`.
//   4. Advisory verdict + rules trace (same conditions as Classic).
//   5. CRYPTOGRAPHIC PROOF — collapsed by default. Expanded view holds
//      the AUDIT ENVELOPE anchors PLUS the connector arguments_hash
//      (moved here so the visible v2 view never shows it twice).
//   6. The non-discovery "canonical mandate body not persisted" note +
//      the same Export JSON / Audit bundle (HTML) buttons as Classic.
// ---------------------------------------------------------------------------

function MandateDetailV2({
  record,
  payload,
  buyerReveal,
  containerLayout,
  siteDomain,
  setView,
}: {
  record: AuditRecord;
  payload?: AuditRecordPayload;
  buyerReveal: 'auto' | 'on_click';
  containerLayout: 'sticky' | 'flat';
  siteDomain?: string;
  setView?: NavToGuardrails;
}): JSX.Element {
  const C = useC();
  const api = useApi();
  const presented = presentDecision(record.decision);
  const is_discovery = presented.chain_class === 'discovery';
  const has_rules_trace = record.rules_evaluated.length > 0;
  const is_connector_record = record.connector_call !== undefined;
  const rules_carry_advisory_surface = record.rules_evaluated.some(
    (r) => (r as { surface?: string }).surface === 'tool_call_advisory',
  );
  const advisory_mode =
    rules_carry_advisory_surface
    || (is_discovery && is_connector_record && has_rules_trace);

  const [proofOpen, setProofOpen] = useState(false);
  const [exportingJson, setExportingJson] = useState(false);
  const [exportingHtml, setExportingHtml] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [bundleSplit, setBundleSplit] = useState<BundleSplit | null>(null);

  const onExportJson = async (): Promise<void> => {
    setExportingJson(true);
    setExportError(null);
    try {
      setBundleSplit(await exportAuditRecordJSON(api, record, siteDomain));
    } catch (err) {
      setExportError(
        exportErrorMessage('Export', err),
      );
    } finally {
      setExportingJson(false);
    }
  };

  const onExportHtml = async (): Promise<void> => {
    setExportingHtml(true);
    setExportError(null);
    try {
      setBundleSplit(await exportAuditBundleHTML(api, record, siteDomain));
    } catch (err) {
      setExportError(
        exportErrorMessage('Bundle export', err),
      );
    } finally {
      setExportingHtml(false);
    }
  };

  const outcome = outcomePresentation(record);
  const headline = composeHeadline(record, payload);
  const agent_identity = identityForHeader(record);

  // `feat/dry-run-ux-polish` (2) — see MandateDetailClassic for the
  // drawer-embed rationale (single outer scrollbar).
  const cardClassName =
    containerLayout === 'flat' ? 'p-5' : 'p-5 sticky top-24 overflow-y-auto';
  const cardStyle =
    containerLayout === 'flat' ? undefined : { maxHeight: 'calc(100vh - 7rem)' };
  return (
    <Card className={cardClassName} {...(cardStyle !== undefined ? { style: cardStyle } : {})}>
      {/* 1. Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Pill tone={outcome.tone}>{outcome.label}</Pill>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
            }}
          >
            {is_discovery && record.skill_id !== undefined
              ? 'TOOL CALL'
              : is_discovery
                ? 'VISIT'
                : 'TRANSACTIONAL'}
          </span>
        </div>
        <h2
          style={{
            fontFamily: serifFont,
            fontSize: 20,
            color: C.text,
            letterSpacing: -0.4,
            lineHeight: 1.2,
            margin: 0,
            marginBottom: 8,
          }}
        >
          {headline}
        </h2>
        <div
          className="flex items-center gap-2 flex-wrap"
          style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}
        >
          <span
            style={{ wordBreak: 'break-all' }}
          >
            {record.record_id}
          </span>
          <span aria-hidden="true">·</span>
          <RelativeTime iso={record.evaluated_at} />
        </div>
      </div>

      {/* 1b. AGENT — discovery-only section. Discovery records carry no
          cart or settlement, so the first meaningful content after the
          header is who the bot is and what it's doing. DiscoveryRows
          renders agent identity (matched/unrecognized), the raw
          User-Agent string, and the one-sentence activity description. */}
      {is_discovery ? (
        <div className="mb-5">
          <dl className="space-y-3">
            <DiscoveryRows C={C} record={record} {...(setView !== undefined ? { setView } : {})} />
          </dl>
        </div>
      ) : null}

      {/* ADR-0077 / UX P1-3 — read an order top-down: the requested
          cart and what was charged come first, then who, then the
          forensic details. Order: LINE ITEMS → SETTLEMENT → BUYER →
          DETAILS → THE EXCHANGE. */}

      {/* 2. LINE ITEMS — the agent's REQUESTED cart (intent_summary).
          Top of the panel so a reviewer sees what was ordered first.
          Omitted on discovery records (no cart) AND on refunds (a refund
          carries no cart — the REFUND block below carries its facts). */}
      {record.intent_summary !== undefined
      && readSettlement(record)?.kind !== 'refund' ? (
        <div className="mb-5">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 8,
            }}
          >
            LINE ITEMS
          </div>
          <RequestedLineItems
            C={C}
            intent={record.intent_summary}
            settlementLines={readSettlement(record)?.line_items}
          />
        </div>
      ) : null}

      {/* 3. SETTLEMENT / REFUND — what was actually moved. ADR-0099: a
          refund-MANDATE record renders the dedicated REFUND block (money
          OUT + original-order back-link + reason + provenance), NOT the
          order SETTLEMENT panel (which is shaped for a charge). */}
      {readSettlement(record)?.kind === 'refund' ? (
        <RefundBlock
          C={C}
          settlement={readSettlement(record)!}
          intent={record.intent_summary}
          decision={record.decision}
        />
      ) : (
        <SettlementPanel
          C={C}
          settlement={readSettlement(record)}
          requestedDiscountCode={record.intent_summary?.discount?.code}
        />
      )}

      <HandoffPanel C={C} handoff={readHandoff(record)} />

      {/* 3b. ORIGINAL ORDER (ADR-0099 follow-up) — for a refund record,
          the reviewable order facts + the buyer live on the ORIGINAL
          order, server-resolved onto `original_order`. Rendered only for
          refunds; the panel carries its OWN buyer reveal (keyed by the
          original record), so the standalone buyer panel below is
          suppressed for refunds to avoid the misleading "no buyer on this
          mandate" message against the buyer-less refund record. */}
      {readSettlement(record)?.kind === 'refund' ? (
        <OriginalOrderPanel C={C} original={readOriginalOrder(record) ?? null} />
      ) : null}

      {/* 4. BUYER DETAIL (ADR-0065 §10) — transactional-only by design
          (see Classic branch). Suppressed on refunds: the buyer rides the
          ORIGINAL order panel above, not the refund record. */}
      {!is_discovery && readSettlement(record)?.kind !== 'refund' ? (
        <BuyerDetailPanel record_id={record.record_id} reveal={buyerReveal} />
      ) : null}

      {/* 5. DETAILS */}
      <div className="mb-5">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 8,
          }}
        >
          DETAILS
        </div>
        <DetailsGrid
          C={C}
          record={record}
          {...(siteDomain !== undefined ? { siteDomain } : {})}
        />
      </div>

      {/* 6. THE EXCHANGE — the decrypted raw request/response capture
          (ADR-0059). Rendered ONLY when a payload was captured/retained;
          for a settled order the cart + settlement + buyer above are the
          evidence, so an empty capture is hidden rather than shown as a
          confusing "nothing here" box. Demoted below DETAILS so it's not
          the first thing a reviewer sees on a settled order. */}
      {payload !== undefined ? (
        <div className="mb-5">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 8,
            }}
          >
            THE EXCHANGE
          </div>
          <PayloadPanel
            C={C}
            payload={payload}
            request_hash={record.request_hash ?? ''}
            response_hash={record.response_hash ?? ''}
          />
        </div>
      ) : null}

      {/* 7. Advisory verdict + rules trace */}
      {advisory_mode ? (
        <AdvisoryVerdictPill C={C} rules={record.rules_evaluated} />
      ) : null}
      {has_rules_trace ? (
        <RulesTrace C={C} record={record} advisory={advisory_mode} />
      ) : null}

      {/* 8. CRYPTOGRAPHIC PROOF — collapsed by default */}
      <div className="mb-5">
        <button
          type="button"
          onClick={() => setProofOpen((v) => !v)}
          aria-expanded={proofOpen}
          className="flex items-center gap-2 w-full"
          style={{
            background: 'transparent',
            border: 'none',
            padding: '6px 0',
            cursor: 'pointer',
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textAlign: 'left',
          }}
        >
          {proofOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          CRYPTOGRAPHIC PROOF
        </button>
        {proofOpen ? (
          <div
            className="p-3 mt-2"
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            <Anchor C={C} label="envelope_signature" value={record.envelope_signature} />
            <Anchor C={C} label="prev_record_hash" value={record.prev_record_hash} />
            <Anchor C={C} label="merkle_root" value={record.merkle_root} />
            {record.connector_call?.arguments_hash !== undefined ? (
              <Anchor
                C={C}
                label="arguments_hash"
                value={record.connector_call.arguments_hash}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Non-discovery: canonical-mandate-body-not-persisted note (same
       *  copy as Classic; the constraint is structural, not cosmetic). */}
      {!is_discovery ? (
        <div
          className="p-3 mb-5"
          style={{
            background: 'transparent',
            border: `1px dashed ${C.borderSoft}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          The canonical signed mandate body (intent, principal, agent_id,
          ed25519 signature, key_id) is verified at the edge and intentionally
          NOT persisted server-side. The audit record carries
          only the sanitized summary above. The chain anchors prove the record
          was committed, not the mandate's contents.
        </div>
      ) : null}

      {/* Export buttons — same as Classic. */}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => void onExportJson()}
          disabled={exportingJson || exportingHtml}
          className="flex items-center justify-center gap-2 px-3 py-2 flex-1"
          style={{
            background: exportingJson ? C.surface2 : C.lime,
            color: exportingJson ? C.muted : C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            cursor: exportingJson || exportingHtml ? 'not-allowed' : 'pointer',
          }}
        >
          <FileJson size={13} /> {exportingJson ? 'Exporting…' : 'Export JSON'}
        </button>
        <button
          type="button"
          onClick={() => void onExportHtml()}
          disabled={exportingJson || exportingHtml}
          className="flex items-center justify-center gap-2 px-3 py-2 flex-1"
          style={{
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            fontFamily: sansFont,
            fontWeight: 500,
            fontSize: 13,
            borderRadius: 2,
            cursor: exportingJson || exportingHtml ? 'not-allowed' : 'pointer',
          }}
        >
          <Download size={13} /> {exportingHtml ? 'Exporting…' : 'Audit bundle (HTML)'}
        </button>
      </div>
      {exportError !== null ? (
        <div
          className="mt-2 p-2 flex items-start gap-2"
          style={{
            background: C.coralDim,
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
          }}
        >
          <AlertOctagon size={12} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.4 }}>
            {exportError}
          </span>
        </div>
      ) : null}
      <BundleDayNote C={C} record={record} />
      <BundlePartsNote C={C} api={api} record={record} siteDomain={siteDomain} split={bundleSplit} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// v2 helpers — outcome derivation, headline composition, friendlier
// labels, and the compact DETAILS grid.
// ---------------------------------------------------------------------------

export interface OutcomePresentation {
  tone: PillTone;
  label: string;
}

/**
 * v2 outcome pill — prefers the connector_call outcome (the merchant-
 * visible "did it work" signal) over the decision (the policy-engine
 * signal), then falls through to the Classic decision-pill mapping when
 * neither is available. Keeps the Classic mapping as the fallback so the
 * panel cannot invent a new state.
 */
export function outcomePresentation(record: AuditRecord): OutcomePresentation {
  // A terminal transactional SETTLEMENT outcome is the operator's headline
  // and MUST agree with the SETTLEMENT panel below — no lime "APPROVED"
  // header sitting over a coral "BLOCKED AT SETTLEMENT" panel (the r31
  // case). Refund records keep their own (kind:'refund') presentation.
  const settlement = readSettlement(record);
  if (
    settlement !== undefined
    && settlement.kind !== 'refund'
    && (settlement.outcome === 'succeeded'
      || settlement.outcome === 'failed'
      || settlement.outcome === 'aborted')
  ) {
    return effectiveOutcome({ decision: record.decision, settlement });
  }
  const call = record.connector_call;
  if (call !== undefined) {
    switch (call.outcome) {
      case 'fulfilled':
        // mint when we have a connector_version (a "real" wired connector
        // round-tripped) so the pill reads as "confirmed"; lime otherwise.
        return {
          tone: call.connector_version !== undefined ? 'mint' : 'lime',
          label: 'FULFILLED',
        };
      case 'connector_rejected':
        return { tone: 'coral', label: 'CONNECTOR REJECTED' };
      case 'backend_unavailable':
        return { tone: 'coral', label: 'BACKEND UNAVAILABLE' };
      case 'pending_integration':
        return { tone: 'amber', label: 'PENDING INTEGRATION' };
      default:
        // Unknown widened outcome — fall through to decision below.
        break;
    }
  }
  const fallback = presentDecision(record.decision);
  return { tone: fallback.tone, label: fallback.label };
}

/**
 * Compose a human headline for the v2 header. Pattern:
 *   <Agent> <friendly-action> <"<query>" when present>
 *
 * Falls back to `recordHeadline(record)` if any piece is missing — the
 * shared headline builder already produces sensible strings for every
 * decision class. Never throws on shape surprises (defensive reads
 * through `unknown`).
 */
export function composeHeadline(
  record: AuditRecord,
  payload?: AuditRecordPayload,
): string {
  const agent_id =
    record.identification_match?.agent_id
    ?? record.rejected_agent_id;
  // Mismatch: the claimed name, in quotes (never the registry display name).
  const id_result = identityState(record);
  const short_who: string | null = (() => {
    if (id_result.state === 'mismatch') return quotedName(id_result.name);
    if (agent_id !== undefined) {
      const full = displayAgentName(agent_id);
      return full.split(' · ').pop() ?? full;
    }
    return null;
  })();
  const skill_id = record.skill_id ?? record.connector_call?.skill_id;
  const action_label = skill_id !== undefined
    ? friendlyActionLabel(skill_id).toLowerCase()
    : null;
  const query = extractRequestQuery(payload);

  // Prefer the actual settled-rail total over the agent-signed cap when
  // the rail dispatched a payment-rail settlement. Surfacing the cap as
  // the headline reads as a discrepancy against the SETTLEMENT panel
  // below ("$50 max" vs "$21.71 total"); the cap stays in the panel as
  // a muted "Authorized max" ceiling line.
  const settled_total = settledTotalForHeadline(record);

  if (short_who !== null && action_label !== null) {
    const base = query !== null
      ? `${short_who} ${action_label} for "${query}"`
      : `${short_who} ${action_label}`;
    return settled_total !== null ? `${base} · ${settled_total}` : base;
  }
  // Fall back to the shared search-index headline — already produces
  // sensible strings for every decision class. `recordHeadline` carries
  // the same settled-total preference internally (via summarizeIntent).
  // `visitListName` quotes a Mismatch and drops the "· signed by …"
  // suffix (signer detail is Raw-data only, labels-v2).
  return visitListName(recordHeadline(record), id_result);
}

/**
 * Read a friendly query string out of the request payload, if present.
 * Probes the common shapes (`{ query }`, `{ q }`, `{ search }`) and
 * returns null if none match. Never throws on shape surprises.
 */
function extractRequestQuery(payload?: AuditRecordPayload): string | null {
  if (payload === undefined) return null;
  const canonical = payload.request?.canonical_json;
  if (canonical === null || typeof canonical !== 'object') return null;
  const obj = canonical as Record<string, unknown>;
  for (const key of ['query', 'q', 'search', 'search_query']) {
    const v = obj[key];
    if (typeof v === 'string' && v.length > 0 && v.length <= 120) {
      return v;
    }
  }
  return null;
}

/**
 * Identity line for the v2 header — short agent display + a tone hint
 * so the merchant can see at a glance whether the visitor was matched,
 * unrecognized, human, or unmatched. Returns null when there's no
 * useful identity signal to surface (e.g. transactional records where
 * the identity sits in the rejected_agent slot we already rendered as
 * `short_who` in the headline).
 */
export function identityForHeader(record: AuditRecord): string | null {
  const cls = record.identification_match?.visitor_class;
  const agent_id = record.identification_match?.agent_id;
  if (cls === 'matched_agent' && agent_id !== undefined) {
    const full = displayAgentName(agent_id);
    const short = full.split(' · ').pop() ?? full;
    return `matched · ${short}`;
  }
  if (cls === 'unknown_agent') {
    const inferred = inferAgentFromUserAgent(record.user_agent);
    if (inferred !== null) {
      const full = displayAgentName(inferred);
      const short = full.split(' · ').pop() ?? full;
      return `unrecognized · likely ${short}`;
    }
    return 'unrecognized';
  }
  if (cls === 'human_likely') {
    return 'human';
  }
  if (record.identification_match?.matched === true && agent_id !== undefined) {
    const full = displayAgentName(agent_id);
    const short = full.split(' · ').pop() ?? full;
    return `matched · ${short}`;
  }
  return null;
}

/**
 * Friendlier policy-version label for the v2 DETAILS grid. Maps the two
 * non-version sentinels the policy engine emits to plain-English copy;
 * any other value is rendered verbatim so unknown versions never get
 * silently rewritten.
 */
function friendlyPolicyVersion(version: string): string {
  // Delegates to the shared shortener so the detail subline collapses the
  // baked-in window timestamps the same way the list card does
  // (`catalog-eligibility-2026-…-v5` → `Rules · version 5`).
  return shortPolicyLabel(version);
}

/**
 * Friendlier retention-class label for the v2 DETAILS grid. Bounded
 * enum — every value is mapped.
 */
function friendlyRetention(cls: AuditRetentionClass): string {
  switch (cls) {
    case 'short_term_30d':
      return '30 days';
    case 'standard_3y':
      return '3 years';
    case 'long_term_7y':
      return '7 years';
    case 'security_1y':
      return '1 year (security)';
  }
}

/**
 * v2 DETAILS — compact two-column-ish key/value group. Each row reuses
 * the existing `Row` atom so the typography matches the rest of the
 * panel. The connector summary lives here (platform/skill/latency/
 * outcome) so the visible v2 view never shows `arguments_hash`; that
 * one moves to the CRYPTOGRAPHIC PROOF disclosure.
 */
/**
 * ADR-0077 — render the agent's REQUESTED cart. Multi-line carts carry
 * `intent.line_items` (one `{quantity} × {sku}` per line, in signed
 * order); legacy single-SKU carts carry `intent.sku` (+ optional
 * top-level `quantity`). Distinct from the SETTLED per-line evidence in
 * the SETTLEMENT panel (ADR-0078) — this is what the agent asked to
 * buy, not what was charged.
 */
/**
 * Resolve a requested SKU to its settled product name, if available.
 *
 * The Shopify rail resolves each SKU → variant → product title at
 * settlement (ADR-0069) and the origin surfaces it per-line in the
 * settlement evidence (ADR-0078, `settlement.line_items[].product_title`).
 * The intent and settlement line arrays may be in different order
 * (Layer-C idempotency reconciliation), so this joins by SKU with an
 * exhaustive search rather than by index.
 *
 * Returns the resolved `product_title` ONLY when a settlement line
 * matches the SKU AND carries a non-empty title; otherwise `undefined`
 * so the caller falls back to the bare SKU. Never fabricates a name:
 * absent on discovery observations, pending escalations, Stripe orders
 * (no catalogue), unresolved variants, and pre-ADR-0069 settlements.
 */
function resolveProductTitle(
  sku: string,
  settlementLines:
    | NonNullable<MandateRecord['settlement']>['line_items']
    | undefined,
): string | undefined {
  if (settlementLines === undefined || settlementLines.length === 0) {
    return undefined;
  }
  const match = settlementLines.find((l) => l.sku === sku);
  if (
    match !== undefined &&
    typeof match.product_title === 'string' &&
    match.product_title.length > 0
  ) {
    return match.product_title;
  }
  return undefined;
}

export function RequestedLineItems({
  C,
  intent,
  settlementLines,
}: {
  C: ReturnType<typeof useC>;
  intent: NonNullable<AuditRecord['intent_summary']>;
  /**
   * Settled per-line evidence (ADR-0078). Used to resolve each requested
   * SKU to its human-readable `product_title` (ADR-0069). Absent on
   * non-settled records (discovery, pending escalations) and Stripe
   * orders (no product catalogue) — those fall back to the bare SKU.
   */
  settlementLines?: NonNullable<MandateRecord['settlement']>['line_items'];
}): JSX.Element {
  const lines = intent.line_items;
  // ADR-0253 2026-10-27 — the signed intent now optionally carries a
  // per-line `name` (AP2 CartMandate `items[i].name` + UCP session
  // `item.title`). Preferred over the Shopify-rail settlement title
  // (ADR-0069) because the signed name is what the merchant agent
  // committed to at mandate time; the settlement rail's title is a
  // post-settle lookup and is absent on Stripe-rail / escalated
  // records. Falls through to the SKU when neither is present.
  const rows =
    lines !== undefined && lines.length > 0
      ? lines.map((l) => ({
          sku: l.sku,
          qty: l.quantity ?? 1,
          ...(typeof (l as { name?: unknown }).name === 'string'
            && ((l as { name?: string }).name as string).length > 0
            ? { signed_name: (l as { name?: string }).name as string }
            : {}),
        }))
      : typeof intent.sku === 'string' && intent.sku.length > 0
        ? [{ sku: intent.sku, qty: intent.quantity ?? 1 }]
        : [];
  if (rows.length === 0) {
    return (
      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>—</span>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {rows.map((r, i) => {
        const title = (r as { signed_name?: string }).signed_name
          ?? resolveProductTitle(r.sku, settlementLines);
        // No resolved title — render the legacy single-line `{qty} × {sku}`
        // shape unchanged (mono, C.text, muted qty marker).
        if (title === undefined) {
          return (
            <div
              key={`${r.sku}:${i}`}
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.text,
                wordBreak: 'break-all',
              }}
            >
              <span style={{ color: C.muted }}>{r.qty} ×</span> {r.sku}
            </div>
          );
        }
        // Resolved title — product NAME (sans, C.text) primary, with the
        // SKU as a muted mono secondary line below. Qty keeps the mono
        // muted marker for layout consistency with the fallback row.
        return (
          <div
            key={`${r.sku}:${i}`}
            style={{ display: 'flex', flexDirection: 'column', gap: 1 }}
          >
            <div style={{ fontSize: 13, color: C.text, wordBreak: 'break-word' }}>
              <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                {r.qty} ×
              </span>{' '}
              {title}
            </div>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                wordBreak: 'break-all',
              }}
            >
              {r.sku}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function DetailsGrid({
  C,
  record,
  siteDomain,
}: {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
  siteDomain?: string;
}): JSX.Element {
  const timeZone = useDisplayTimeZone();
  const call = record.connector_call;
  const intent = record.intent_summary;
  return (
    <dl className="space-y-3">
      <Row
        C={C}
        label="Site"
        tooltip="The site this visit hit. Each site you register in Sill gets its own record scope — only your account can see this site's audit records."
      >
        {siteDomain !== undefined ? (
          <div>
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
              {siteDomain}
            </span>
            <div
              style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 2, wordBreak: 'break-all' }}
            >
              {record.site_id}
            </div>
          </div>
        ) : (
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              color: C.text,
              wordBreak: 'break-all',
            }}
          >
            {record.site_id}
          </span>
        )}
      </Row>

      {/* The signed mandate id (`mnd_…`). Shown whenever the record
          carries one (every transactional/signed mandate, incl. refunds)
          so the operator can cross-reference the signed authorization —
          the v2 grid previously omitted it entirely. */}
      {record.mandate_id !== undefined ? (
        <Row
          C={C}
          label="Mandate ID"
          tooltip="The signed mandate this record authorizes. For a refund this is the refund mandate; the original order it unwinds is shown separately."
        >
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              color: C.text,
              wordBreak: 'break-all',
            }}
          >
            {record.mandate_id}
          </span>
        </Row>
      ) : null}

      {intent !== undefined ? (
        <Row C={C} label="Merchant">
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              wordBreak: 'break-all',
            }}
          >
            {intent.merchant}
          </span>
        </Row>
      ) : null}

      {/* Principal intentionally NOT rendered as a DETAILS row: the signed
          `principal` block is never persisted server-side (ADR-0029 §4.1),
          so this cell is 100% boilerplate on every record, transactional
          or discovery — never real data. The "canonical mandate body ...
          intentionally NOT persisted" note below the DETAILS grid already
          states this once, so a per-record row would just repeat it. */}

      {call !== undefined ? (
        <Row C={C} label="Connector">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              color: C.text,
              lineHeight: 1.5,
            }}
          >
            {/* `connector_version` already encodes `platform@version`
                (e.g. "shopify@1"); show it directly and fall back to the
                bare platform for unwired records — avoids the doubled
                "shopify@shopify@1" the previous concatenation produced. */}
            <span>{call.connector_version ?? call.platform}</span>
            {call.skill_id !== undefined ? (
              <>
                <span style={{ color: C.muted, margin: '0 6px' }}>·</span>
                <span>{call.skill_id}</span>
              </>
            ) : null}
            {call.latency_ms_backend !== undefined ? (
              <>
                <span style={{ color: C.muted, margin: '0 6px' }}>·</span>
                <span>{call.latency_ms_backend.toLocaleString('en-US')}ms</span>
              </>
            ) : null}
            {call.reason !== undefined ? (
              <div style={{ color: C.muted, marginTop: 2, fontSize: 11 }}>
                reason: {call.reason}
              </div>
            ) : null}
          </div>
        </Row>
      ) : null}

      {intent !== undefined ? (
        <>
          <Row C={C} label="Action">
            <div>
              <span
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.text,
                  fontWeight: 600,
                }}
              >
                {friendlyActionLabel(intent.action)}
              </span>
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  marginTop: 2,
                }}
              >
                {intent.action}
              </div>
            </div>
          </Row>
          {/* Authorized ceiling — relabeled from "Max amount" because the
              prior label read as a charged total against the SettlementPanel
              below, which carries the actual `total_minor`. When the
              SettlementPanel is rendering its own "Authorized max" row
              (settlement.ceiling_minor present), this row is suppressed so
              the same ceiling figure does not appear twice. */}
          {readSettlement(record)?.ceiling_minor === undefined ? (
            <Row C={C} label="Authorized max">
              <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>
                {formatIntentAmount(intent.max_amount, intent.currency)}
              </span>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 10,
                  color: C.muted,
                  marginTop: 2,
                  lineHeight: 1.4,
                }}
              >
                Agent ceiling, not a charged total.
              </div>
            </Row>
          ) : null}
          <Row C={C} label="Summary">
            <span
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.4,
              }}
            >
              {describeIntent(intent)}
            </span>
          </Row>
        </>
      ) : null}

      {record.decision === 'verification_rejected'
        && typeof record.reject_reason === 'string'
        && record.reject_reason.length > 0 ? (
        <Row C={C} label="Reject reason">
          <div>
            <span
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.coralFg,
                fontWeight: 600,
              }}
            >
              {friendlyRejectReason(record.reject_reason)}
            </span>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                marginTop: 2,
              }}
            >
              {record.reject_reason}
            </div>
          </div>
        </Row>
      ) : null}

      <Row C={C} label="Policy">
        <span
          style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}
          title={record.policy_version} /* title-ok: full value behind the shortened label */
        >
          {friendlyPolicyVersion(record.policy_version)}
        </span>
      </Row>
      <Row C={C} label="Retention">
        <Pill tone="neutral">{friendlyRetention(record.retention_class)}</Pill>
      </Row>
      <Row C={C} label="Evaluated at">
        <div style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>
          {formatIsoDate(record.evaluated_at, timeZone)}
        </div>
        <RelativeTime
          iso={record.evaluated_at}
          className="block"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            marginTop: 2,
          }}
        />
      </Row>
    </dl>
  );
}

/** Sub-component for the bot-classification row (ADR-0204). Needs its own
 *  state for the expand/collapse toggle + dismiss-button interaction, so it
 *  can't live inside an IIFE.
 *
 *  Dismiss flow (ADR-0204 follow-up): the classifier's `action` string is a
 *  merchant-facing recommendation cached globally per bot. Once a merchant
 *  has acted on the recommendation, they can dismiss it per-account,
 *  per-`agent_key` via `POST /v1/bots/:agent_key/dismiss-action`. A
 *  dismissed action renders as a muted "Action dismissed" line instead of
 *  the recommendation body.
 *
 *  One-click action buttons (ADR-0204 follow-up 2): each action_type
 *  renders an inline affordance the merchant can act on without
 *  copy-pasting from prose —
 *    - `robots_txt_block`      → "Copy robots.txt snippet" (writes
 *                                 the User-agent + Disallow lines to
 *                                 the clipboard).
 *    - `sill_policy_reject`    → inline "Add block rule" confirm →
 *                                 append an r01 reject on the active
 *                                 policy without leaving the panel.
 *    - `sill_policy_escalate`  → inline "Add escalation rule" confirm →
 *                                 same shape, `action_on_match:'escalate'`.
 *    - `none`                  → no button.
 *  Buttons are hidden while `dismissed` (merchant explicitly hid it) OR
 *  `ruleAdded` (rule just added — the recommendation is resolved). */
export function BotClassificationRow({
  C,
  summary,
  dismissed,
  onDismiss,
  setView,
  agentId,
}: {
  C: ReturnType<typeof useC>;
  summary: BotSummary;
  /** True when the current account has already dismissed the action for
   *  this bot's agent_key. When true, the action row renders as
   *  "Action dismissed" (muted) with no further recommendation. */
  dismissed: boolean;
  /** Invoked when the merchant clicks the Dismiss button. The parent
   *  performs the POST + updates the local `dismissed` state. Absent when
   *  the record carries no agent_key (dismiss surface unavailable). */
  onDismiss?: () => void;
  /** Route to a top-level view. Threaded from `MandateDetail` so the
   *  "Add block/escalation rule" CTA can open the Guardrails view
   *  pre-filled. Absent when the detail panel is mounted without routing
   *  context (dry-run drawer). */
  setView?: NavToGuardrails;
  /** Registered Sill `agent_<slug>` id of the identified visitor from
   *  `record.identification_match.agent_id`. Pre-filled into the r01
   *  allowlist when the merchant navigates to Guardrails. Absent when
   *  the visitor wasn't matched to a registered agent — in that case
   *  the inline confirm is suppressed. */
  agentId?: string;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const THRESHOLD = 100;
  const isTruncated = summary.action.length > THRESHOLD;
  // Trim at a word boundary so the cut doesn't land mid-token.
  const action_display =
    !expanded && isTruncated
      ? (summary.action.slice(0, THRESHOLD).replace(/\s+\S*$/, '') || summary.action.slice(0, THRESHOLD)) + '…'
      : summary.action;
  const verdict_bg =
    summary.verdict === 'safe'
      ? C.limeDim
      : summary.verdict === 'block'
        ? C.coralDim
        : C.amberDim;

  // Default to 'none' for records classified before action_type shipped.
  const action_type = summary.action_type ?? 'none';
  const robots_agent_token = summary.robots_agent_token;

  const onCopyRobots = (): void => {
    if (robots_agent_token === undefined) return;
    const snippet = `User-agent: ${robots_agent_token}\nDisallow: /`;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(snippet);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Which one-click button to render alongside the recommendation prose.
  // `robots_txt_block` needs the token to build a valid snippet; if it's
  // missing (older classifier output), fall back to text-only.
  const showRobotsButton =
    action_type === 'robots_txt_block' && robots_agent_token !== undefined;
  // Inline policy-rule adder — the confirm flow (idle → confirming →
  // saving → success | error) lives in `BotPolicyRuleAdder`. Gated on
  // BOTH the identity being present (no `agent_id` → nothing to
  // allowlist — the DB projection matches only known registry entries,
  // Show the "Add block/escalation rule" CTA only when:
  //   - the classifier recommends a policy rule (not robots_txt_block / none)
  //   - we have routing context to navigate to Guardrails
  //   - the visitor is a registered Sill agent with a valid agent_id to allowlist
  // Web crawlers get action_type='robots_txt_block' (not sill_policy_*) so they
  // never reach this branch. Malformed ids are also gated out.
  const AGENT_ID_RE = /^agent_[a-z0-9_]{1,64}$/;
  // DISABLED 2026-10-15: the CTA pre-fills an r01 ALLOWLIST with this agent,
  // which lets the agent through and rejects every OTHER agent — the opposite
  // of "block". Hidden until a real deny rule / firewall block replaces it
  // (threat alerts + firewall mode ADR, slice S5).
  const POLICY_ADDER_ENABLED = false;
  const showPolicyAdder =
    POLICY_ADDER_ENABLED
    && (action_type === 'sill_policy_reject' || action_type === 'sill_policy_escalate')
    && setView !== undefined
    && agentId !== undefined
    && AGENT_ID_RE.test(agentId);

  const resolved = dismissed;
  return (
    <Row
      C={C}
      label="Bot classification"
      labelIcon={<Sparkles size={9} style={{ color: C.muted, flexShrink: 0 }} />}
      tooltip="Automated bot classification by Claude AI. This verdict is informational — it doesn't affect your guardrail rules."
      tooltipDir="up"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Pill
            tone={
              summary.verdict === 'safe'
                ? 'lime'
                : summary.verdict === 'block'
                  ? 'coral'
                  : 'amber'
            }
          >
            {summary.verdict.toUpperCase()}
          </Pill>
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, fontWeight: 600 }}>
            {summary.who}
          </span>
        </div>
        <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
          {summary.why}
        </span>
        {resolved ? (
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 11,
              color: C.muted,
              fontStyle: 'italic',
              lineHeight: 1.5,
            }}
          >
            Action dismissed
          </span>
        ) : (
          <div
            style={{
              background: verdict_bg,
              borderRadius: 3,
              padding: '5px 8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
            }}
          >
            <span
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                lineHeight: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              {action_display}
              {isTruncated && (
                <button
                  onClick={() => setExpanded((e) => !e)}
                  style={{
                    display: 'inline',
                    marginLeft: 6,
                    fontFamily: sansFont,
                    fontSize: 11,
                    color: C.muted,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  {expanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </span>
            {onDismiss !== undefined && (
              <Tooltip content="Hide this recommendation once you've acted on it" style={{ flexShrink: 0 }}>
              <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss recommendation"
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  letterSpacing: 1.2,
                  color: C.muted,
                  background: 'none',
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  padding: '2px 6px',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  flexShrink: 0,
                }}
              >
                Dismiss
              </button>
              </Tooltip>
            )}
          </div>
        )}
        {/* ── ONE-CLICK ACTION (ADR-0204 follow-up) ── the classifier's
             `action_type` drives WHICH affordance renders. Hidden while
             resolved (dismissed OR rule-added) so an action the merchant
             has already taken doesn't re-offer itself. Fully absent for
             `action_type === 'none'` (safe/well-known bots) — no button,
             just the prose above. */}
        {!resolved && showRobotsButton ? (
          <div>
            <button
              type="button"
              onClick={onCopyRobots}
              aria-label={copied ? 'Copied' : 'Copy robots.txt snippet'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                padding: '4px 8px',
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {copied ? <Check size={10} /> : <Copy size={10} />}
              {copied ? 'Copied!' : 'Copy robots.txt snippet'}
            </button>
          </div>
        ) : null}
        {showPolicyAdder && !dismissed ? (
          <BotPolicyRuleAdder
            C={C}
            agentId={agentId as string}
            who={summary.who}
            actionType={
              action_type as 'sill_policy_reject' | 'sill_policy_escalate'
            }
            setView={setView as NavToGuardrails}
          />
        ) : null}
      </div>
    </Row>
  );
}

/**
 * `BotPolicyRuleAdder` — one-click CTA that navigates to the Guardrails
 * view with the AddRuleModal pre-filled for the identified bot agent.
 *
 * Clicking "Add block rule" or "Add escalation rule" calls setView with
 * pre-fill opts so the Guardrails view opens the rule editor with:
 *   - rule type r01 (agent_identity allowlist) pre-selected
 *   - the identified agent_id pre-filled in the allowlist textarea
 *   - action_on_match set to reject (block) or escalate
 *
 * The merchant reviews the pre-filled form, edits if needed, and clicks
 * "Add Rule" in the Guardrails modal. This is the same flow as the
 * skill-manifest CTA (ManifestChangeCard → open r18 modal pre-filled).
 */
function BotPolicyRuleAdder({
  C,
  agentId,
  who,
  actionType,
  setView,
}: {
  C: ReturnType<typeof useC>;
  agentId: string;
  who: string;
  actionType: 'sill_policy_reject' | 'sill_policy_escalate';
  setView: NavToGuardrails;
}): JSX.Element {
  const isReject = actionType === 'sill_policy_reject';
  const ctaLabel = isReject ? 'Add block rule' : 'Add escalation rule';
  const ctaTitle = isReject
    ? `Open Rules to add a block rule for ${who}`
    : `Open Rules to add an escalation rule for ${who}`;

  const openGuardrailsPrefilled = (): void => {
    setView('guardrails', {
      preset_rule_type: 'r01',
      preset_agent_id: agentId,
      preset_action_on_match: isReject ? 'reject' : 'escalate',
    });
  };

  return (
    <div>
      <Tooltip content={ctaTitle}>
      <button
        type="button"
        onClick={openGuardrailsPrefilled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          color: C.text,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          padding: '4px 8px',
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        {ctaLabel} →
      </button>
      </Tooltip>
    </div>
  );
}

export interface RowsProps {
  C: ReturnType<typeof useC>;
  record: AuditRecord;
}

/** ADR-0204 follow-up — DiscoveryRows extends RowsProps with an optional
 *  `setView` so BotClassificationRow's one-click policy/escalation
 *  actions can navigate to the top-level guardrails view. Kept out of
 *  the shared `RowsProps` interface so `TransactionalRows` and
 *  `RulesTrace` (which don't need it) keep their existing signatures. */
export interface DiscoveryRowsProps extends RowsProps {
  setView?: NavToGuardrails;
}

export function DiscoveryRows({ C, record, setView }: DiscoveryRowsProps): JSX.Element {
  // Render based on the ADR-0034 visitor_class taxonomy first so the
  // right-side panel agrees with the row card's pill. Falls back to the
  // pre-ADR-0034 matched/unmatched binary for legacy records that lack
  // visitor_class (server-side classifier ran after 2026-06-01).
  const cls = record.identification_match?.visitor_class;
  const matched = record.identification_match?.matched === true;
  const agent_id = record.identification_match?.agent_id;
  const inferredId = cls === 'unknown_agent'
    ? inferAgentFromUserAgent(record.user_agent)
    : null;
  const unknownBotLabel: string | undefined = cls === 'unknown_agent'
    ? (
        inferredId !== null
          ? (displayAgentName(inferredId).split(' · ').pop() ?? undefined)
          : (extractCompatibleBotName(record.user_agent) ?? undefined)
      )
    : undefined;
  const rescuedBotId = cls === 'human_likely'
    ? inferAgentFromUserAgent(record.user_agent)
    : null;
  const rescuedBotName: string | undefined = cls === 'human_likely'
    ? (rescuedBotId !== null
        ? (displayAgentName(rescuedBotId).split(' · ').pop() ?? undefined)
        : (extractCompatibleBotName(record.user_agent) ?? undefined))
    : undefined;
  const isRescuedBot = rescuedBotId !== null || rescuedBotName !== undefined;
  // Visits panel Step A (labels-v2): `cf_verified_bot` never feeds identity
  // (it described Sill's own host on Worker rows). The UA heuristic only
  // picks a neutral "Likely purpose" label below; it is never a pill or a
  // verdict. The identity mark comes from `identityState()` alone.
  const spoofSignal = computeSpoofSignal(record.user_agent, undefined, cls, record.signature_verified, record.signed_agent, record.signed_credential_name_registered);
  const idResult = identityState(record);
  const isMismatch = idResult.state === 'mismatch';

  // ── Inferred type computation (used both in identity block and standalone row) ──
  const ua = (record.user_agent ?? '').toLowerCase();
  const pagePath = (record.page_path ?? '').toLowerCase();
  let inferredTypeLabel: string | null = null;
  let inferredTypeDetail: string | null = null;
  if (cls === 'unknown_agent' || (cls === 'human_likely' && isRescuedBot)) {
    if (/ahrefs|mj12bot|semrush|majestic|dotbot|moz\.com/.test(ua)) {
      inferredTypeLabel = 'SEO audit tool';
      inferredTypeDetail = 'Third-party SEO crawlers that index your site\'s link graph and metadata for marketing analysis.';
    } else if (/twitterbot|facebookexternalhit|linkedinbot|slackbot|discordbot|facebot|meta-external/.test(ua)) {
      inferredTypeLabel = 'Social link preview';
      inferredTypeDetail = 'Fetches page metadata (title, image, description) when a user shares your URL on a social platform.';
    } else if (/upptime|pingdom|monitor|statuspage|sill-status/.test(ua)) {
      inferredTypeLabel = 'Uptime monitor';
      inferredTypeDetail = 'External service checking whether your site is reachable. Expected traffic.';
    } else if (/gptbot|claudebot|claude|perplexity|mistral|reflectionbot|ccbot|llama/.test(ua)) {
      inferredTypeLabel = 'AI training / retrieval crawler';
      inferredTypeDetail = 'Web crawler operated by an AI company, fetching content for training data or search indexing.';
    } else if (
      /nmap|masscan|nikto|sqlmap|shodan|zgrab|nuclei|acunetix|burp/.test(ua)
      || /\.(env|git|htaccess|htpasswd|sql|bak|backup|conf|config|log|swp)/.test(pagePath)
      || /\/(phpmyadmin|wp-admin|admin|\.well-known\/security\.txt|\.git\/)/.test(pagePath)
      || (/wp-content\/plugins/.test(pagePath) && spoofSignal !== null)
    ) {
      inferredTypeLabel = 'Security scanner / vulnerability probe';
      inferredTypeDetail = 'Automated tool probing for known vulnerabilities, exposed config files, or CMS weaknesses. Not inherently malicious — security researchers, pen-testers, and malicious actors all use the same tooling.';
    } else if (/curl|wget|python-requests|axios|node-fetch|go-http|okhttp|httpie/.test(ua)) {
      inferredTypeLabel = 'Developer HTTP client';
      inferredTypeDetail = 'A scripted HTTP request, likely from a developer or automation pipeline. May be a custom integration or a one-off probe.';
    } else if (/baiduspider|yandexbot|sogou|duckduckbot|applebot/.test(ua)) {
      inferredTypeLabel = 'Search engine crawler';
      inferredTypeDetail = 'Web crawler operated by a search engine for indexing. Standard traffic.';
    } else if (spoofSignal === 'bare_ua' || spoofSignal === 'empty_ua') {
      inferredTypeLabel = 'Automated tool — identity concealed';
      inferredTypeDetail = 'The visitor deliberately omitted its identity. Could be a scraper, scanner, or custom integration that prefers to stay anonymous.';
    } else if (spoofSignal === 'typo_ua') {
      inferredTypeLabel = 'Scraper with fake browser header';
      inferredTypeDetail = 'Uses a hardcoded browser-like string that contains a known error ("Mozlila"). Common in mass-scraping tools with a static, incorrect UA.';
    }
  }

  // ── Description line shown below the agent name in the identity block ──
  const activity = agent_id !== undefined ? AGENT_ACTIVITY[agent_id] : undefined;
  const identityDescription: string | null = (() => {
    if (activity !== undefined) return activity;
    if (inferredTypeDetail !== null) return inferredTypeDetail;
    if (cls === 'unknown_agent') {
      return 'Not a recognized AI agent or crawler. Could be a new bot, a custom integration, or standard web traffic.';
    }
    if (cls === 'human_likely' && isRescuedBot) return 'Logged as a browser visit at ingest. The identity string identifies a known crawler — new visits will be classified correctly.';
    if (cls === 'human_likely') {
      const device = humanRecordDevice(record);
      const lead = device !== null ? `${device}. ` : '';
      return `${lead}A standard browser visit. Some AI tools use stock browser identities and will appear here.`;
    }
    return null;
  })();

  // Mismatch reason (server evidence only) appended under the description.
  const verdictLine: { text: string; color: string } | null = (() => {
    const reason = mismatchReason(record, idResult);
    return reason !== null ? { text: reason, color: C.coralFg } : null;
  })();

  return (
    <>
      {/* ── IDENTITY BLOCK — full-width, no "AGENT" row label ── */}
      <div
        style={{
          paddingBottom: 14,
          marginBottom: 2,
          borderBottom: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="flex items-start gap-3">
          <div style={{ marginTop: 3, flexShrink: 0 }}>
            {isMismatch || (cls === 'human_likely' && !isRescuedBot) ? (
              <VisitLogoGlyph record={record} result={idResult} size={20} />
            ) : (
            <ProviderLogo
              agent_id={
                (cls === 'matched_agent' || (cls === undefined && matched))
                  ? agent_id
                  : cls === 'unknown_agent'
                    ? inferredId
                    : cls === 'human_likely' && isRescuedBot
                      ? rescuedBotId
                      : null
              }
              visitor_class={
                (cls === 'matched_agent' || (cls === undefined && matched))
                  ? 'matched_agent'
                  : cls === 'human_likely' && !isRescuedBot
                    ? 'human_likely'
                    : 'unknown_agent'
              }
              size={20}
              fallbackColor={
                (cls === 'matched_agent' || (cls === undefined && matched))
                  ? C.limeFg
                  : C.muted
              }
              {...(unknownBotLabel !== undefined
                ? { label: unknownBotLabel }
                : cls === 'human_likely' && rescuedBotName !== undefined
                  ? { label: rescuedBotName }
                  : {})}
            />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {/* Name + status pills */}
            <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 6 }}>
              <span style={{ fontFamily: sansFont, fontSize: 14, color: C.text, fontWeight: 700, lineHeight: 1.3 }}>
                {isMismatch ? quotedName(idResult.name) : (cls === 'matched_agent' || (cls === undefined && matched))
                  ? displayAgent(agent_id)
                  : cls === 'unknown_agent'
                    ? (inferredId !== null ? (inferredId.startsWith('agent_sill_') ? displayAgent(inferredId) : `Likely ${displayAgent(inferredId)}`) : (unknownBotLabel ?? 'Unidentified visitor'))
                    : cls === 'human_likely' && isRescuedBot
                      ? (rescuedBotId !== null ? `Likely ${displayAgent(rescuedBotId)}` : (rescuedBotName ?? 'Unidentified visitor'))
                      : cls === 'human_likely'
                        ? humanRecordName(record)
                        : 'Unknown visitor'}
              </span>
              <VisitIdentityMark record={record} result={idResult} />
              {isMismatch ? null : (cls === 'matched_agent' || (cls === undefined && matched)) ? (
                <Tooltip
                  content="Found in Sill's agent registry: it announced itself with a known AI agent or crawler name."
                  side="top"
                >
                  <Pill tone="lime">MATCHED</Pill>
                </Tooltip>
              ) : (cls === 'unknown_agent' || (cls === 'human_likely' && isRescuedBot)) ? (
                <>
                  <Tooltip
                    content="We detected automated (non-browser) traffic, but this visitor doesn't match any known agent in our registry. Could be a new AI agent, a scraper, or an unknown crawler."
                    side="top"
                  >
                    <Pill tone="neutral">UNRECOGNIZED</Pill>
                  </Tooltip>
                </>
              ) : cls === 'human_likely' ? (
                <Pill tone="neutral">HUMAN</Pill>
              ) : null}
            </div>
            {/* Description (Mismatch: the server's reason only — the
                registry description belongs to the real agent). */}
            {identityDescription !== null || verdictLine !== null ? (
              <span style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.55, display: 'block' }}>
                {isMismatch ? null : identityDescription}
                {verdictLine !== null ? (
                  <strong style={{ color: verdictLine.color, display: 'block', marginTop: 4 }}>
                    {verdictLine.text}
                  </strong>
                ) : null}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* ── VISIT — page, search term, action ── */}
      {record.page_path !== undefined && record.page_path.length > 0 ? (
        <Row C={C} label="Page visited">
          <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, wordBreak: 'break-all' }}>
            {record.page_path}
          </span>
        </Row>
      ) : null}
      {(() => {
        if (record.page_query === undefined || record.page_query.length === 0) return null;
        const SEARCH_KEYS = ['q', 's', 'search', 'query', 'keyword', '_search'];
        let term: string | null = null;
        try {
          const params = new URLSearchParams(record.page_query.startsWith('?') ? record.page_query.slice(1) : record.page_query);
          for (const k of SEARCH_KEYS) {
            const v = params.get(k);
            if (v !== null && v.trim().length > 0) { term = v.trim(); break; }
          }
        } catch { /* malformed query string */ }
        if (term === null) return null;
        return (
          <Row C={C} label="Search term">
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>{term}</span>
          </Row>
        );
      })()}
      <Row C={C} label="Action">
        {record.skill_id !== undefined && record.skill_id.length > 0 ? (
          <div>
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
              {`Tool call · ${friendlyActionLabel(record.skill_id)}`}
            </span>
            <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 2 }}>
              {record.skill_id}
            </div>
          </div>
        ) : (
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
            Observed only — no tool called.
          </span>
        )}
      </Row>

      {/* ── LOCATION — city / region / country (when available) ── */}
      {(record.geo_city !== undefined || record.geo_region !== undefined || record.geo_country !== undefined) ? (
        <Row C={C} label="Location">
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CountryFlag code={record.geo_country} withName={false} />
            <span>{[record.geo_city, record.geo_region, countryName(record.geo_country)].filter(Boolean).join(', ')}</span>
          </span>
        </Row>
      ) : null}

      {/* ── INFERRED TYPE (unknown bots only, when no activity description available) ── */}
      {inferredTypeLabel !== null && activity === undefined ? (
        <Row C={C} label="Likely purpose">
          <div>
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, fontWeight: 600 }}>
              {inferredTypeLabel}
            </span>
          </div>
        </Row>
      ) : null}

      {/* ── BROWSER SIGNATURE — technical detail, shown last ── */}
      {record.user_agent !== undefined && record.user_agent.length > 0 ? (
        <Row
          C={C}
          label="Identity string"
          tooltip="The raw identifier this visitor sent with its request. Browsers include their name, version, and OS. Bots usually include their own name. This is the primary signal Sill uses to classify traffic."
        >
          <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, wordBreak: 'break-all', lineHeight: 1.4 }}>
            {record.user_agent}
          </span>
        </Row>
      ) : null}

      {/* ── BOT CLASSIFICATION (ADR-0204) — Haiku classifier verdict.
           Threads `agentId` so BotPolicyRuleAdder can open Guardrails
           pre-filled for the matched agent. Absent when the visitor
           wasn't matched; in that case only prose + dismiss render. ── */}
      {/* Never on a Mismatch: the AI box's robots.txt snippet and "Add
          block rule" target the CLAIMED name / agent_id, i.e. the real
          agent this sender impersonates (security review Step A m1). */}
      {record.ai_summary !== undefined && !isMismatch ? (
        <BotActionDismissController
          C={C}
          summary={record.ai_summary}
          agent_key={record.agent_key}
          initial_dismissed={record.bot_action_dismissed === true}
          {...(record.identification_match?.agent_id !== undefined
            ? { agentId: record.identification_match.agent_id }
            : {})}
          {...(setView !== undefined ? { setView } : {})}
        />
      ) : null}
    </>
  );
}

/**
 * Owns the "action dismissed" local state + the dismiss POST. Kept
 * co-located with `BotClassificationRow` so the pure presentational
 * component stays free of network wiring — the parent handles the
 * fire-and-forget call and the optimistic state flip.
 *
 * `agent_key` is projected onto the single-record audit detail
 * (`packages/types/src/audit.ts`). Absent when the row has no matching
 * `agent_observed` entry (pre-classifier records, records with no
 * user_agent). In that case the dismiss button is hidden — there is no
 * key to dismiss against.
 */
export function BotActionDismissController({
  C,
  summary,
  agent_key,
  initial_dismissed,
  setView,
  agentId,
}: {
  C: ReturnType<typeof useC>;
  summary: BotSummary;
  agent_key?: string | undefined;
  initial_dismissed: boolean;
  /** Threaded from `MandateDetail`; forwarded to `BotClassificationRow`
   *  so the "Add block/escalation rule" CTA can open Guardrails pre-filled. */
  setView?: NavToGuardrails;
  /** Identified agent_id from `record.identification_match.agent_id` —
   *  forwarded so `BotPolicyRuleAdder` can pre-fill the r01 allowlist.
   *  Absent when the visitor wasn't matched to a registered agent. */
  agentId?: string;
}): JSX.Element {
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const [dismissed, setDismissed] = useState(initial_dismissed);
  // Server-side the dismiss-action route is owner/admin only (2026-10-02
  // security review). Hide the Dismiss affordance for reviewer / viewer;
  // legacy test scaffolds that mount without `<AuthProvider>` get a
  // null auth and keep the pre-existing behaviour (dismiss visible).
  const role =
    auth !== null && auth.state.kind === 'authenticated'
      ? auth.state.user.role
      : null;
  const roleAllowsDismiss =
    role === null || role === 'owner' || role === 'admin';
  const canDismiss =
    http !== null && agent_key !== undefined && roleAllowsDismiss;

  const onDismiss = (): void => {
    // Optimistic flip — the endpoint is a 204 idempotent upsert on
    // (account_id, agent_key), so the failure mode of a lost POST is a
    // reappearance on next fetch. That trade-off is intentional:
    // dismissing a recommendation must feel instant.
    setDismissed(true);
    if (http === null || agent_key === undefined) return;
    void http
      .request({
        method: 'POST',
        path: `/v1/bots/${encodeURIComponent(agent_key)}/dismiss-action`,
      })
      .catch(() => {
        // Best-effort; the row simply reappears on next fetch. No
        // console.log — the network layer already logs.
      });
  };

  return (
    <BotClassificationRow
      C={C}
      summary={summary}
      dismissed={dismissed}
      {...(canDismiss ? { onDismiss } : {})}
      {...(setView !== undefined ? { setView } : {})}
      {...(agentId !== undefined ? { agentId } : {})}
    />
  );
}

function TransactionalRows({ C, record }: RowsProps): JSX.Element {
  const intent = record.intent_summary;
  const is_verification_rejected = record.decision === 'verification_rejected';
  return (
    <>
      <Row C={C} label="Mandate ID">
        {record.mandate_id !== undefined ? (
          <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, wordBreak: 'break-all' }}>
            {record.mandate_id}
          </span>
        ) : (
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
            (not recorded)
          </span>
        )}
      </Row>
      {is_verification_rejected && record.rejected_agent_id !== undefined ? (
        <Row C={C} label="Agent">
          <div className="flex items-start gap-2">
            <div style={{ marginTop: 2 }}>
              <ProviderLogo
                agent_id={record.rejected_agent_id}
                visitor_class="matched_agent"
                size={16}
                fallbackColor={C.coralFg}
              />
            </div>
            <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
              <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
                {displayAgentName(record.rejected_agent_id)}
              </span>
              <Pill tone="coral">REJECTED</Pill>
            </div>
          </div>
          <span style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, display: 'block', marginTop: 4 }}>
            Agent that attempted this rejected mandate. Identity captured
            from the envelope before signature verification failed.
          </span>
        </Row>
      ) : null}
      {is_verification_rejected
        && typeof record.reject_reason === 'string'
        && record.reject_reason.length > 0 ? (
        <Row C={C} label="Reject reason">
          <div>
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, fontWeight: 600 }}>
              {friendlyRejectReason(record.reject_reason)}
            </span>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                marginTop: 2,
              }}
            >
              {record.reject_reason}
            </div>
          </div>
        </Row>
      ) : null}
      {intent !== undefined ? (
        <>
          <Row C={C} label="Action">
            <div>
              <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
                {friendlyActionLabel(intent.action)}
              </span>
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  marginTop: 2,
                }}
              >
                {intent.action}
              </div>
            </div>
          </Row>
          <Row C={C} label="Merchant">
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, wordBreak: 'break-all' }}>
              {intent.merchant}
            </span>
          </Row>
          <Row C={C} label="SKU">
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.text, wordBreak: 'break-all' }}>
              {intent.sku}
            </span>
          </Row>
          {/* Authorized ceiling — renamed from "Max amount" to avoid being
              misread as a charged total. Suppressed entirely when the
              SettlementPanel will render its own "Authorized max" row
              (settlement.ceiling_minor present) so the ceiling appears
              exactly once on the panel. */}
          {readSettlement(record)?.ceiling_minor === undefined ? (
            <Row C={C} label="Authorized max">
              <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}>
                {formatIntentAmount(intent.max_amount, intent.currency)}
              </span>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 10,
                  color: C.muted,
                  marginTop: 2,
                  lineHeight: 1.4,
                }}
              >
                Agent ceiling, not a charged total.
              </div>
            </Row>
          ) : null}
          <Row C={C} label="Summary">
            <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.4 }}>
              {describeIntent(intent)}
            </span>
          </Row>
        </>
      ) : (
        <Row C={C} label="Intent">
          <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
            {is_verification_rejected
              ? 'Not recorded — signature verification failed before the mandate\'s intent was trusted.'
              : 'Not recorded — this mandate pre-dates intent capture. Newly-evaluated mandates include it.'}
          </span>
        </Row>
      )}
    </>
  );
}

export function RulesTrace({
  C,
  record,
  advisory,
}: RowsProps & { advisory: boolean }): JSX.Element {
  const rules = record.rules_evaluated;
  // The rule whose action decided the outcome; later rules never ran.
  const decidingRule = rules.find((r) => {
    const st = ruleResultState(r);
    return st === 'failed' || st === 'exempted';
  })?.rule_id;
  return (
    <div
      className="p-3 mb-5"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 6,
        }}
      >
        {advisory ? `RULES EVALUATED · ADVISORY · ${rules.length}` : `RULES EVALUATED · ${rules.length}`}
      </div>
      {advisory ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.45,
            marginBottom: 8,
          }}
        >
          This call was observed under Discovery mode. The verdict below
          is what the active policy would have decided; it did not gate
          the call.
        </div>
      ) : null}
      {rules.length === 0 ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.4,
          }}
        >
          The active policy had no rules — the mandate fell through to the
          default outcome.
        </div>
      ) : (
        <ul className="space-y-2">
          {rules.map((r, i) => {
            const state = ruleResultState(r);
            // Color the action chip by semantic class. 'allow' is the
            // neutral safe path; 'allow_exemption' is a deliberate
            // exception (amber to flag it); 'reject' and 'escalate' use
            // the standard tones; 'evaluator_error' is coral because
            // fail-closed maps to rejected; 'none' is neutral.
            const actionTone: { fg: string; bg: string } = (() => {
              switch (r.action_taken) {
                case 'allow':
                  return { fg: C.limeFg, bg: C.limeDim };
                case 'allow_exemption':
                  return { fg: C.amberFg, bg: C.amberDim };
                case 'reject':
                case 'evaluator_error':
                  return { fg: C.coralFg, bg: C.coralDim };
                case 'escalate':
                  return { fg: C.amberFg, bg: C.amberDim };
                case 'none':
                default:
                  return { fg: C.muted, bg: 'transparent' };
              }
            })();
            return (
              <li
                key={`${r.rule_id}-${i}`}
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  color: C.text,
                  lineHeight: 1.4,
                }}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span style={{ color: C.text, fontWeight: 600, wordBreak: 'break-all' }}>
                    {r.rule_id}
                  </span>
                  <Tooltip content={RULE_STATE_TOOLTIP[state]}>
                  <span
                    data-rule-state={state}
                    style={{
                      color: state === 'passed' ? C.limeFg
                        : state === 'exempted' ? C.amberFg
                        : state === 'failed' ? C.coralFg
                        : C.muted,
                      fontSize: 10,
                      cursor: 'help',
                    }}
                  >
                    {RULE_STATE_LABEL[state]}
                  </span>
                  </Tooltip>
                  {/* A rule that never ran took no action: no NONE chip. */}
                  {state !== 'not_checked' ? (
                  <Tooltip content={RULE_ACTION_TOOLTIP[r.action_taken]}>
                  <span
                    style={{
                      fontSize: 9,
                      letterSpacing: 1,
                      color: actionTone.fg,
                      background: actionTone.bg,
                      padding: '1px 5px',
                      border: `1px solid ${actionTone.bg}`,
                      borderRadius: 2,
                      cursor: 'help',
                    }}
                  >
                    {r.action_taken.toUpperCase().replace('_', ' ')}
                  </span>
                  </Tooltip>
                  ) : null}
                </div>
                {r.reason !== undefined && r.reason.length > 0 ? (
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 11,
                      color: C.muted,
                      marginTop: 2,
                      marginLeft: 0,
                      lineHeight: 1.4,
                    }}
                  >
                    {state === 'not_checked'
                      ? (decidingRule !== undefined
                        ? `Skipped: ${decidingRule} already decided this request.`
                        : 'Skipped: an earlier rule already decided this request.')
                      : r.reason}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Row({
  C,
  label,
  labelIcon,
  tooltip,
  tooltipDir = 'down',
  children,
}: {
  C: { muted: string; borderSoft: string };
  label: string;
  /** Optional icon rendered inline after the label kicker (e.g. Sparkles for AI rows). */
  labelIcon?: React.ReactNode;
  /** Optional hover tooltip on the label kicker — uses the shared `Tooltip` atom. */
  tooltip?: string;
  /** Preferred vertical anchor; the atom still flips when it needs to. */
  tooltipDir?: 'down' | 'up';
  children: React.ReactNode;
}): JSX.Element {
  const labelStyle: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 10,
    color: C.muted,
    letterSpacing: 1.5,
    paddingTop: 2,
    cursor: tooltip !== undefined ? 'help' : undefined,
    ...(labelIcon !== undefined ? { display: 'inline-flex', alignItems: 'center', gap: 3 } : {}),
  };
  const labelBody = (
    <>
      {label.toUpperCase()}
      {labelIcon}
    </>
  );
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-1 pb-3"
      style={{ borderBottom: `1px solid ${C.borderSoft}` }}
    >
      <dt style={{ margin: 0 }}>
        {tooltip !== undefined ? (
          <Tooltip content={tooltip} side={tooltipDir === 'up' ? 'top' : 'bottom'} displayMode="inline">
            <span style={labelStyle}>{labelBody}</span>
          </Tooltip>
        ) : (
          <span style={labelStyle}>{labelBody}</span>
        )}
      </dt>
      <dd className="sm:col-span-2">{children}</dd>
    </div>
  );
}

/**
 * `ConnectorCallPanel` — ADR-0037 §6.2 + §"Open questions" item 11.
 *
 * Renders the bridged-connector evidence persisted under
 * `audit_record.discovery_context.connector_call`. Shown when a
 * record carries the field — produced by the dispatch handler in
 * `services/api/src/http/routes/internal/connectors-dispatch.ts` for
 * `'bridged_connector'` queue drafts.
 *
 * Outcome → pill tone mapping (ADR-0037 §6.2 + the semantic palette
 * in CLAUDE.md §4):
 *
 *   - `fulfilled`            → lime    (approve / success)
 *   - `connector_rejected`   → coral   (reject / merchant-visible no)
 *   - `backend_unavailable`  → amber   (escalate / transient)
 *   - `pending_integration`  → sky     (deferred — ADR-0038 will wire)
 *
 * Gate 8 guarantee: the panel renders ONLY the bounded `reason`
 * class (a closed enum) — NEVER the raw backend status or message,
 * even if the server response somehow carries them. The
 * `AuditConnectorCall` TypeScript projection deliberately omits the
 * `backend_status` / `backend_message` / `backend_request_id` fields
 * so a future server-side leak doesn't reach the render.
 */
function ConnectorCallPanel({
  C,
  call,
}: {
  C: ReturnType<typeof useC>;
  call: AuditConnectorCall;
}): JSX.Element {
  const tone: PillTone = (() => {
    switch (call.outcome) {
      case 'fulfilled':
        return 'lime';
      case 'connector_rejected':
        return 'coral';
      case 'backend_unavailable':
        return 'amber';
      case 'pending_integration':
        return 'sky';
      default:
        // Unknown outcome (server-side enum widened ahead of the
        // dashboard) — render as neutral. assertNever is not used here
        // because the dashboard's JSONB-derived field is a wire shape
        // that could expand; neutral is the safe fallback.
        return 'neutral';
    }
  })();
  const label = call.outcome.replace(/_/g, ' ').toUpperCase();
  return (
    <div
      className="p-3 mb-5"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          CONNECTOR CALL
        </div>
        <Pill tone={tone}>{label}</Pill>
      </div>
      <dl
        className="space-y-2"
        style={{ fontFamily: monoFont, fontSize: 11, color: C.text }}
      >
        <ConnectorRow C={C} label="Platform">
          {call.platform}
          {call.connector_version !== undefined ? (
            <span style={{ color: C.muted, marginLeft: 6 }}>
              {call.connector_version}
            </span>
          ) : null}
        </ConnectorRow>
        {call.skill_id !== undefined ? (
          <ConnectorRow C={C} label="Skill">
            {call.skill_id}
          </ConnectorRow>
        ) : null}
        {call.reason !== undefined ? (
          <ConnectorRow C={C} label="Reason">
            {call.reason}
          </ConnectorRow>
        ) : null}
        {call.latency_ms_backend !== undefined ? (
          <ConnectorRow C={C} label="Latency">
            {call.latency_ms_backend.toLocaleString('en-US')} ms
          </ConnectorRow>
        ) : null}
        {call.arguments_hash !== undefined ? (
          <ConnectorRow C={C} label="Arguments hash">
            <span style={{ wordBreak: 'break-all' }}>{call.arguments_hash}</span>
          </ConnectorRow>
        ) : null}
      </dl>
    </div>
  );
}

function ConnectorRow({
  C,
  label,
  children,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid grid-cols-3 gap-3">
      <dt
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </dt>
      <dd
        className="col-span-2"
        style={{ fontFamily: monoFont, fontSize: 11, color: C.text }}
      >
        {children}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SettlementPanel — ADR-0063 / ADR-0066 money-breakdown surface.
//
// Reads settlement evidence projected from `discovery_context.settlement`
// JSONB (UNSIGNED — same off-signing-scope posture as `connector_call`
// and `origin_match`). The dashboard's wire shape for this field lives
// on the local `MandateRecord` extension in `./types.ts`; the canonical
// signed `AuditRecord` doesn't declare it.
//
// Rendered ONLY when:
//   - the wire field is present, AND
//   - the rail is a payment rail (`'stripe' | 'shopify'`), AND
//   - at least one money field (`subtotal_minor` / `tax_minor` /
//     `shipping_minor` / `total_minor` / `ceiling_minor`) is present.
//
// The panel mirrors the design density of `ConnectorCallPanel`: thin
// 1px border, 2px radius, mono uppercase kicker with letter-spacing,
// right-aligned amounts in a clean two-column list. Total is visually
// separated by a top border + bolder type so a reviewer's eye lands on
// the buyer-facing charge. Authorized ceiling is muted, framed as
// agent headroom (`total ≤ ceiling`), not a competing total.
//
// `shipping_minor` is omitted when ABSENT or `0` — the field is
// optional today and showing `$0.00` would imply "$0 shipping was
// charged" instead of "shipping isn't wired on this record". When the
// rail records a non-zero shipping line, the row renders.
// ---------------------------------------------------------------------------

type SettlementEvidence = NonNullable<MandateRecord['settlement']>;

/**
 * Read the optional `settlement` sub-object off a wire audit record.
 *
 * The component param is typed against the canonical `AuditRecord`
 * (which doesn't declare `settlement`); the live wire shape is
 * `MandateRecord` from `./types.ts`, structurally a superset. This
 * helper performs the narrow + cast in one place so callers don't
 * sprinkle `as` everywhere.
 */
export function readSettlement(record: AuditRecord): SettlementEvidence | undefined {
  return (record as MandateRecord).settlement;
}

type HandoffEvidence = NonNullable<MandateRecord['handoff']>;

/**
 * ADR-0174 §7 addendum — read the optional `handoff` sub-object off a
 * wire audit record. Same narrow+cast pattern as `readSettlement`;
 * deliberately a SEPARATE field, never merged with `settlement`.
 */
export function readHandoff(record: AuditRecord): HandoffEvidence | undefined {
  return (record as MandateRecord).handoff;
}

/**
 * ADR-0099 follow-up — read the optional server-resolved `original_order`
 * block off a refund-MANDATE wire record. Same narrow+cast pattern as
 * `readSettlement`: the component param is the canonical `AuditRecord`,
 * the live wire shape is `MandateRecord`. Returns `undefined` on non-
 * refund records (and when the server could not resolve the original),
 * `null` when present-but-unresolvable, else the projected block.
 */
export function readOriginalOrder(
  record: AuditRecord,
): EscalationOriginalOrder | null | undefined {
  return (record as MandateRecord).original_order;
}

/**
 * Read the optional UCP checkout `order_breakdown` sub-object off a wire
 * audit record. The server attaches it on `GET /v1/audit/:record_id` for
 * UCP-settled orders (`inbound_protocol === 'ucp'`); it is the richest
 * money breakdown the dashboard has access to (per-line price, shipping
 * title, tax place, total). Same narrow+cast pattern as
 * `readSettlement`; UNSIGNED, display-only.
 */
export function readOrderBreakdown(
  record: AuditRecord,
): MandateRecord['order_breakdown'] {
  return (record as MandateRecord).order_breakdown;
}

/** ADR-0099 — humanize the bounded refund reason_code enum. */
function humanizeReasonCode(code: string | undefined): string | null {
  if (code === undefined) return null;
  switch (code) {
    case 'requested_by_customer': return 'Requested by customer';
    case 'defective': return 'Defective';
    case 'not_received': return 'Not received';
    case 'wrong_item': return 'Wrong item';
    case 'other': return 'Other';
    default: return null;
  }
}

/** ADR-0099 — derive refund provenance from the SIGNED decision (no new
 *  wire field): a human resolved an escalation, or it auto-approved
 *  under the cap. */
function refundProvenanceLabel(decision: AuditRecord['decision']): string {
  switch (decision) {
    case 'escalated_approved': return 'Approved via escalation (human review)';
    case 'escalated_rejected': return 'Rejected via escalation (human review)';
    case 'approved': return 'Auto-approved under cap (no human review)';
    case 'rejected':
    case 'rejected_post_verify':
    case 'verification_rejected': return 'Rejected';
    default: return String(decision);
  }
}

/**
 * ADR-0099 — REFUND block for a refund-MANDATE record. Renders money
 * OUT (Refunded vs the signed cap vs the original order total), the
 * reason, the provenance (HITL vs auto), the original-order back-link,
 * and the Shopify refund reference. A failed/aborted refund suppresses
 * the dollar figure and shows only the coral pill (never "looks
 * refunded" next to a failure). Mirrors the SETTLEMENT panel's chrome.
 */
export function RefundBlock({
  C,
  settlement,
  intent,
  decision,
}: {
  C: ReturnType<typeof useC>;
  settlement: SettlementEvidence;
  intent: AuditRecord['intent_summary'] | undefined;
  decision: AuditRecord['decision'];
}): JSX.Element {
  const currency = settlement.currency ?? 'USD';
  const succeeded = settlement.outcome === 'succeeded' || settlement.outcome === 'reconciled';
  const failed = settlement.outcome === 'failed' || settlement.outcome === 'aborted';
  const cap_minor =
    intent !== undefined && typeof intent.max_amount === 'number'
      ? Math.round(intent.max_amount * 100)
      : undefined;
  const reason = humanizeReasonCode(settlement.reason_code);
  const pillTone = failed ? C.coralFg : C.amberFg;
  const pillBg = failed ? C.coralDim : C.amberDim;
  const pillLabel = failed ? 'REFUND FAILED' : succeeded ? 'REFUNDED' : 'REFUND PENDING';

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 2 }}>
          REFUND
        </div>
        <span
          style={{
            fontFamily: monoFont, fontSize: 9, letterSpacing: 1,
            color: pillTone, background: pillBg,
            border: `1px solid ${pillTone}`, borderRadius: 2, padding: '1px 6px',
          }}
        >
          {pillLabel}
        </span>
      </div>
      <dl
        className="space-y-1 p-3"
        style={{ border: `1px solid ${C.border}`, borderRadius: 2 }}
      >
        {/* Money: suppress the dollar on a failed refund (render-defense
            — never show a refunded amount next to a FAILED pill). */}
        {succeeded && typeof settlement.refunded_minor === 'number' ? (
          <SettlementRow C={C} label="Refunded" amount={`-${formatMinor(settlement.refunded_minor, currency)}`} tone="coral" />
        ) : null}
        {cap_minor !== undefined ? (
          <SettlementRow C={C} label="Refund cap (signed)" amount={formatMinor(cap_minor, currency)} muted />
        ) : null}
        {typeof settlement.original_total_minor === 'number' ? (
          <SettlementRow C={C} label="Original order total" amount={formatMinor(settlement.original_total_minor, currency)} muted />
        ) : null}

        {/* Metadata lines (mono) — reason, provenance, original-order
            back-link, Shopify refund reference. */}
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
          {reason !== null ? (
            <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginBottom: 4 }}>
              Reason · {reason}
            </div>
          ) : null}
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginBottom: 4 }}>
            {refundProvenanceLabel(decision)}
          </div>
          {settlement.original_mandate_id !== undefined ? (
            <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginBottom: 4, wordBreak: 'break-all' }}>
              Original (Sill mandate) · <span style={{ color: C.text }}>{settlement.original_mandate_id}</span>
            </div>
          ) : null}
          {settlement.shopify_refund_id !== undefined ? (
            <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, wordBreak: 'break-all' }}>
              Shopify refund · <span style={{ color: C.text }}>{settlement.shopify_refund_id}</span>
            </div>
          ) : null}
          {/* ADR-0100 — the Stripe refund id (`re_…`); links to the refund
              in the Stripe dashboard so the operator can find it. */}
          {settlement.stripe_refund_id !== undefined ? (
            <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, wordBreak: 'break-all' }}>
              Stripe refund ·{' '}
              <a
                href={`https://dashboard.stripe.com/refunds/${encodeURIComponent(settlement.stripe_refund_id)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: C.limeFg, textDecoration: 'underline' }}
              >
                {settlement.stripe_refund_id} ↗
              </a>
            </div>
          ) : null}
        </div>
      </dl>
    </div>
  );
}

/**
 * ADR-0174 §7 addendum — renders the pending-payment order Sill created
 * on a merchant's WooCommerce store on a `handoff_pending` mandate. NOT
 * a settlement panel — deliberately its own component reading its own
 * `handoff` field, so a handoff mandate can never visually read as
 * "settled" no matter how the two panels evolve independently.
 *
 * Much simpler than `SettlementPanel` by construction: no refund /
 * dispute / failure-detail states apply — a handoff record has exactly
 * one status (`pending_payment`) until Slice 2/3 ships post-payment
 * visibility (deferred; see ADR-0174 §Deferred F.2/F.3).
 */
export function HandoffPanel({
  C,
  handoff,
}: {
  C: ReturnType<typeof useC>;
  handoff: HandoffEvidence | undefined;
}): JSX.Element | null {
  if (handoff === undefined) return null;
  const currency = handoff.currency ?? 'USD';
  const amount = handoff.amount_minor;
  const has_amount = typeof amount === 'number' && Number.isFinite(amount);
  const line_items = handoff.line_items;
  const has_line_items = Array.isArray(line_items) && line_items.length > 0;

  return (
    <div
      className="p-3 mb-5"
      data-testid="handoff-panel"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          HANDOFF
        </div>
        <Pill tone="sky">AWAITING BUYER PAYMENT</Pill>
      </div>
      <div
        style={{
          fontSize: 12,
          color: C.muted,
          marginBottom: 10,
          lineHeight: 1.5,
        }}
      >
        Sill created a pending order on the merchant&rsquo;s WooCommerce
        store. No payment has moved — the buyer completes checkout on the
        merchant&rsquo;s own site.
      </div>
      {has_amount ? (
        <dl
          className="space-y-1 p-3"
          style={{ border: `1px solid ${C.border}`, borderRadius: 2 }}
        >
          <SettlementRow
            C={C}
            label="Order total"
            amount={formatMinor(amount as number, currency)}
            emphasized
          />
        </dl>
      ) : null}
      {has_line_items ? (
        <div style={{ marginTop: 10 }}>
          {(line_items as NonNullable<HandoffEvidence['line_items']>).map(
            (li, i) => (
              <div
                key={`${li.sku}:${i}`}
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  color: C.text,
                  marginBottom: 2,
                }}
              >
                <span style={{ color: C.muted }}>{li.quantity} ×</span>{' '}
                {li.product_title !== undefined && li.product_title.length > 0
                  ? li.product_title
                  : li.sku}
              </div>
            ),
          )}
        </div>
      ) : null}
      {handoff.wc_order_pay_url !== undefined ? (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <a
            href={handoff.wc_order_pay_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.limeFg,
              textDecoration: 'underline',
              wordBreak: 'break-all',
            }}
          >
            Order-pay link ↗
          </a>
        </div>
      ) : null}
      {handoff.wc_order_id !== undefined ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            marginTop: 6,
          }}
        >
          WooCommerce order ·{' '}
          <span style={{ color: C.text }}>{handoff.wc_order_id}</span>
        </div>
      ) : null}
    </div>
  );
}

interface SettlementPanelProps {
  C: ReturnType<typeof useC>;
  settlement: SettlementEvidence | undefined;
  /**
   * ADR-0075 — the agent-REQUESTED coupon code from the signed intent
   * (`intent_summary.discount.code`). Stripe is payment-only and does
   * NOT honor coupon codes, so for a Stripe-rail order we surface the
   * requested code as intent — explicitly "not applied" — distinct from
   * the Shopify honored-discount row driven by `settlement.discount_*`.
   * Passed from the detail view; the row renders ONLY for the Stripe
   * rail, only when this code is present, and only when there is no
   * honored discount on the settlement.
   */
  requestedDiscountCode?: string | undefined;
}

export function SettlementPanel({
  C,
  settlement,
  requestedDiscountCode,
}: SettlementPanelProps): JSX.Element | null {
  if (settlement === undefined) return null;
  // A rail-neutral pre-dispatch abort (ADR-0151 — r31 aborts BEFORE
  // `railSelector.select()` runs, so the evidence carries `outcome:'aborted'`
  // and NO rail discriminant) MUST still render — the operator needs to see
  // WHY no charge happened. `is_predispatch_abort` widens the accept-gate to
  // that one shape; every other rail-less settlement (unknown/forward-compat)
  // continues to drop (a rail-less "succeeded" is nonsensical and we won't
  // guess a payment breakdown for it).
  const is_predispatch_abort =
    settlement.rail === undefined && settlement.outcome === 'aborted';
  if (
    settlement.rail !== 'stripe'
    && settlement.rail !== 'shopify'
    && !is_predispatch_abort
  ) {
    return null;
  }
  const currency = settlement.currency ?? 'USD';

  // Pull the money fields once. Any of them MAY be absent on
  // pre-ADR-0063 rows; the panel renders only the present ones and
  // never shows `NaN` / `undefined`.
  const subtotal = settlement.subtotal_minor;
  const tax = settlement.tax_minor;
  const shipping = settlement.shipping_minor;
  // ADR-0075 — discount evidence. `discount_minor` is the honored
  // amount (≥ 0); `discount_code` is the merchant promo code Shopify
  // honored. The row renders only when both the amount is present AND
  // > 0 (same omit-when-zero discipline as `shipping_minor`).
  const discount = settlement.discount_minor;
  const discount_code = settlement.discount_code;
  const total = settlement.total_minor;
  const ceiling = settlement.ceiling_minor;

  // RENDER-DEFENSE (Fix #1, layer 3) — on a failed / aborted settlement
  // the money breakdown (Total / subtotal / tax / shipping / discount /
  // per-line) MUST NOT render even if a value somehow rode through on the
  // wire. `total_minor` is the SETTLED total; a dollar "Total" next to a
  // coral FAILED pill reads as a successful charge — dangerous on a money
  // product. The coral FAILED/ABORTED pill + the failure_reason/message
  // line + any payment reference convey the state; no dollar figure does.
  // The `Authorized max` ceiling row is the agent-signed cap (NOT a
  // settled amount), so it still renders on a failure — it frames "what
  // was authorized" without implying anything was charged.
  const suppress_money =
    settlement.outcome === 'failed' || settlement.outcome === 'aborted';

  // ADR-0078 — per-line settlement evidence. The per-line section
  // renders only when the rail recorded MORE THAN ONE line (a
  // single-SKU cart keeps the compact money-breakdown view; a
  // reconciliation hit that predates the FIND_BY_TAG widening has no
  // line_items and also keeps the compact view). Suppressed on a
  // failed/aborted settlement.
  const line_items = settlement.line_items;
  const has_line_items =
    !suppress_money && Array.isArray(line_items) && line_items.length > 1;

  const has_subtotal =
    !suppress_money && typeof subtotal === 'number' && Number.isFinite(subtotal);
  const has_tax =
    !suppress_money && typeof tax === 'number' && Number.isFinite(tax);
  // Per spec: shipping row renders ONLY when present AND > 0. Absent /
  // zero → omit row entirely so we don't imply "$0 shipping was
  // charged" when the field simply isn't wired yet.
  const has_shipping =
    !suppress_money
    && typeof shipping === 'number'
    && Number.isFinite(shipping)
    && shipping > 0;
  // ADR-0075 — same omit-when-zero discipline as shipping: the
  // Discount row renders only when `discount_minor` is a finite
  // positive number. Showing `-$0.00` would imply "a discount was
  // applied for $0" instead of "no discount applied"; the absent /
  // zero case skips the row entirely.
  const has_discount =
    !suppress_money
    && typeof discount === 'number'
    && Number.isFinite(discount)
    && discount > 0;
  const has_total =
    !suppress_money && typeof total === 'number' && Number.isFinite(total);
  const has_ceiling = typeof ceiling === 'number' && Number.isFinite(ceiling);

  // ADR-0075 — agent-REQUESTED coupon, Stripe rail only. Stripe is
  // payment-only and never honors a coupon code (it charges the cart
  // total), so we surface the signed intent without fabricating a
  // discount amount. Render the requested-coupon row ONLY when:
  //   - the rail is Stripe, AND
  //   - the signed intent carried a non-empty coupon code, AND
  //   - there is NO honored discount on the settlement (`has_discount`).
  // The Shopify path is untouched: it shows the honored `Discount`
  // row from `discount_minor`/`discount_code` and never this row.
  const requested_code =
    typeof requestedDiscountCode === 'string'
      ? requestedDiscountCode.trim()
      : '';
  const has_requested_coupon =
    !suppress_money
    && settlement.rail === 'stripe'
    && requested_code.length > 0
    && !has_discount;

  // Failure detail — the decline / abort reason (friendly-cased) + the
  // Stripe decline message. Both ride bounded + non-PII on the wire
  // (server allow-lists the reason against the rail enum; length-caps +
  // control-strips the message). Composed as
  // "<friendly reason> — <message>" when both present, else whichever
  // is present.
  //
  // The raw reason token can arrive either as `failure_reason` (the
  // rail-extracted wire field) OR as `reason` (the raw JSONB key written
  // by a pre-dispatch abort like r31, which fires BEFORE the rail
  // dispatcher populates `failure_reason`). We read either. A
  // pre-dispatch-abort reason (`is_predispatch_abort`) routes through
  // the settlement-abort humanizer (e.g. r31 renders a full sentence:
  // "Cross-agent organization budget exceeded (r31) — the organization's
  // rolling spend cap across all agents was reached, so no charge was
  // made."); every other failure reason keeps the existing sentence-case
  // friendly transform. Unknown reason values NEVER crash — the humanizer
  // falls back to underscore-friendly text.
  const raw_reason_token =
    typeof settlement.failure_reason === 'string'
      && settlement.failure_reason.length > 0
      ? settlement.failure_reason
      : typeof settlement.reason === 'string' && settlement.reason.length > 0
        ? settlement.reason
        : '';
  const failure_reason_label =
    raw_reason_token.length === 0
      ? ''
      : is_predispatch_abort
        ? humanizeSettlementAbortReason(raw_reason_token)
        : friendlyFailureReason(raw_reason_token);
  const failure_message_text =
    typeof settlement.failure_message === 'string'
      && settlement.failure_message.length > 0
      ? settlement.failure_message
      : '';
  const failure_detail =
    failure_reason_label.length > 0 && failure_message_text.length > 0
      ? `${failure_reason_label} — ${failure_message_text}`
      : failure_reason_label.length > 0
        ? failure_reason_label
        : failure_message_text;
  const has_failure_detail = failure_detail.length > 0;

  // ADR-0082 §5 — refund / dispute evidence (post-settlement webhook
  // notifications recorded on the audit row). The charge SUCCEEDED and was
  // then refunded/disputed, so the money breakdown still renders (these are
  // NOT failure/abort outcomes and `suppress_money` stays false); this layers
  // the refunded/disputed state + amount on top. Amounts arrive bounded
  // non-negative ints from the wire's allow-list extractor.
  const refunded =
    settlement.outcome === 'refunded'
    && typeof settlement.refunded_minor === 'number'
    && Number.isFinite(settlement.refunded_minor)
    && settlement.refunded_minor > 0
      ? settlement.refunded_minor
      : null;
  const has_refund = refunded !== null;
  // Full vs partial: a refund covering the whole settled total reads as
  // REFUNDED; less reads as PARTIALLY REFUNDED. When the total is unknown a
  // refund reads as full (there is nothing to be "partial" against).
  const is_full_refund =
    has_refund && (!has_total || (refunded as number) >= (total as number));
  // Net settled = total − refunded (floored at 0). Shows the money that
  // actually stayed with the merchant. Only when the total is known.
  const net_minor =
    has_refund && has_total
      ? Math.max(0, (total as number) - (refunded as number))
      : null;
  const is_disputed = settlement.outcome === 'disputed';
  const disputed_amount =
    is_disputed
    && typeof settlement.disputed_minor === 'number'
    && Number.isFinite(settlement.disputed_minor)
    && settlement.disputed_minor > 0
      ? settlement.disputed_minor
      : null;
  // Friendly-case the Stripe dispute reason (`product_not_received` →
  // "product not received"). Bounded + control-stripped server-side.
  const dispute_reason_label =
    typeof settlement.dispute_reason === 'string'
      && settlement.dispute_reason.length > 0
      ? settlement.dispute_reason.replace(/_/g, ' ')
      : '';

  // Stripe payment reference (success). Prefer the PaymentIntent id
  // (`pi_…`); fall back to the charge id (`ch_…`) when the PI id is
  // absent (legacy reconciliation rows). ONLY on the Stripe rail and
  // ONLY when the outcome is not a failure (a declined/aborted charge
  // has no settled payment reference to link to). Bounded server-side.
  const is_failure_outcome =
    settlement.outcome === 'failed' || settlement.outcome === 'aborted';
  const stripe_pi =
    typeof settlement.stripe_payment_intent_id === 'string'
      && settlement.stripe_payment_intent_id.length > 0
      ? settlement.stripe_payment_intent_id
      : '';
  const stripe_ch =
    typeof settlement.stripe_charge_id === 'string'
      && settlement.stripe_charge_id.length > 0
      ? settlement.stripe_charge_id
      : '';
  const stripe_payment_reference =
    settlement.rail === 'stripe' && !is_failure_outcome
      ? stripe_pi.length > 0
        ? stripe_pi
        : stripe_ch.length > 0
          ? stripe_ch
          : null
      : null;

  // Nothing to show — settlement sub-object was present but carried no
  // money fields AND no failure detail (e.g. an `aborted` rail outcome
  // with only `outcome`). Skip the panel rather than render an empty
  // card. A failed / aborted outcome WITH a failure reason/message
  // still renders (the coral pill + detail are the payload).
  if (
    !has_subtotal
    && !has_tax
    && !has_shipping
    && !has_discount
    && !has_total
    && !has_ceiling
    && !has_failure_detail
    && !has_refund
    && !is_disputed
  ) {
    return null;
  }

  // Over-ceiling case: rail reconciliation reported a total > ceiling
  // (rare — the rail's defense-in-depth check normally aborts before
  // dispatch). Surface as coral on the Authorized max row so a
  // reviewer spots the breach. `total ≤ ceiling` is the normal path.
  const over_ceiling =
    has_total && has_ceiling && (total as number) > (ceiling as number);

  // Rail label — omitted on a pre-dispatch abort (no rail was chosen,
  // so the rail pill would be a lie / read as `undefined`).
  const rail_label =
    settlement.rail !== undefined ? settlement.rail.toUpperCase() : null;

  return (
    <div
      className="p-3 mb-5"
      data-testid="settlement-panel"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          SETTLEMENT
        </div>
        {rail_label !== null ? <Pill tone="neutral">{rail_label}</Pill> : null}
        {/* P1 — rail outcome state. `paid`/`pending` is the financial
            status; a failed/aborted outcome is surfaced loudly (coral)
            so the money breakdown is never read as a clean success when
            it wasn't. Both already ride on the settlement wire. */}
        {settlement.financial_status !== undefined && !has_refund ? (
          // The extractor already drops financial_status on a refunded row;
          // the `!has_refund` guard is defense-in-depth so a refund never
          // shows a PAID pill next to REFUNDED regardless of the wire.
          <Pill tone={settlement.financial_status === 'paid' ? 'mint' : 'amber'}>
            {settlement.financial_status.toUpperCase()}
          </Pill>
        ) : null}
        {is_predispatch_abort ? (
          // ADR-0151 — pre-dispatch abort (r31 today). No rail was
          // selected, so the coral pill reads as "BLOCKED AT SETTLEMENT"
          // rather than a rail-outcome ABORTED. Same coral tone as every
          // other reject; distinct label so the operator reads it as a
          // Sill-gate block (no dispatch attempted), not a rail decline.
          <Pill tone="coral">BLOCKED AT SETTLEMENT</Pill>
        ) : settlement.outcome === 'failed' || settlement.outcome === 'aborted' ? (
          <Pill tone="coral">{settlement.outcome.toUpperCase()}</Pill>
        ) : null}
        {/* Failure detail — the decline / abort reason (friendly-cased)
            + the Stripe decline message when present, e.g.
            "card_declined — Your card was declined". Bounded + non-PII
            (the server allow-lists the reason against the rail enum and
            length-caps + control-strips the message). Rendered next to
            the coral FAILED / ABORTED pill.

            SUPPRESSED for a pre-dispatch abort (`is_predispatch_abort`):
            the dedicated blocked-callout below carries the SAME reason in a
            fuller form, so rendering it here too duplicated the sentence. */}
        {(settlement.outcome === 'failed' || settlement.outcome === 'aborted')
          && has_failure_detail
          && !is_predispatch_abort ? (
          <span
            data-testid="settlement-failure-detail"
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.coralFg,
            }}
          >
            {failure_detail}
          </span>
        ) : null}
        {/* ADR-0082 §5 — post-settlement state. A refund (full / partial)
            renders an amber pill; a dispute renders a coral pill + the
            friendly-cased Stripe reason. The charge's PAID status (when
            present) still shows alongside for disputes — it WAS paid, then
            contested; a refunded order shows REFUNDED instead of PAID. */}
        {has_refund ? (
          <Pill tone="amber">
            {is_full_refund ? 'REFUNDED' : 'PARTIALLY REFUNDED'}
          </Pill>
        ) : null}
        {is_disputed ? <Pill tone="coral">DISPUTED</Pill> : null}
        {is_disputed && dispute_reason_label.length > 0 ? (
          <span
            data-testid="settlement-dispute-reason"
            style={{ fontFamily: monoFont, fontSize: 11, color: C.coralFg }}
          >
            {dispute_reason_label}
          </span>
        ) : null}
      </div>
      {/* ADR-0151 — pre-dispatch abort (r31) explanation block. The inline
          coral pill + reason above is easy to miss next to the other chips;
          a dedicated full-width callout states plainly that the mandate
          passed policy but was blocked at settlement with NO charge, then
          gives the humanized reason. Mirrors the export-error callout
          pattern (coral-dim surface + AlertOctagon). */}
      {is_predispatch_abort ? (
        <div
          data-testid="settlement-blocked-callout"
          className="mb-3 p-3 flex items-start gap-2"
          style={{
            background: C.coralDim,
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
          }}
        >
          <AlertOctagon size={13} color={C.coralFg} className="flex-shrink-0 mt-0.5" />
          <div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              Passed policy — blocked at settlement. No charge was made.
            </div>
            {has_failure_detail ? (
              <div
                data-testid="settlement-blocked-reason"
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  color: C.muted,
                  lineHeight: 1.5,
                  marginTop: 4,
                }}
              >
                {failure_detail}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {has_line_items ? (
        // ADR-0078 — per-line cart breakdown for a multi-line order.
        // One row per line: "<title> (<sku>) ×<qty>" with the line
        // total. Rendered ABOVE the aggregate money breakdown so a
        // reviewer reads the cart first, then the subtotal/tax/total.
        <div className="mb-3">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 4,
            }}
          >
            LINE ITEMS ({line_items!.length})
          </div>
          <dl
            className="space-y-1"
            style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}
          >
            {line_items!.map((li) => (
              <SettlementRow
                key={`${li.variant_id}:${li.sku}`}
                C={C}
                label={`${li.product_title} (${li.sku}) ×${li.quantity}`}
                amount={formatMinor(li.line_total_minor, currency)}
              />
            ))}
          </dl>
        </div>
      ) : null}
      <dl
        className="space-y-1"
        style={{ fontFamily: monoFont, fontSize: 12, color: C.text }}
      >
        {has_subtotal ? (
          <SettlementRow
            C={C}
            label="Subtotal"
            amount={formatMinor(subtotal as number, currency)}
          />
        ) : null}
        {has_discount ? (
          // ADR-0075 — honored coupon-code discount. Label includes
          // the merchant promo code when present (`Discount (SUMMER25)`);
          // amount renders as the negative of the formatted minor
          // value (e.g. `-$3.00`) so a reviewer reads it as money
          // subtracted from the subtotal rather than money charged.
          <SettlementRow
            C={C}
            label={
              typeof discount_code === 'string' && discount_code.length > 0
                ? `Discount (${discount_code})`
                : 'Discount'
            }
            amount={`-${formatMinor(discount as number, currency)}`}
          />
        ) : null}
        {has_requested_coupon ? (
          // ADR-0075 — Stripe-rail requested coupon. NOT a money row:
          // Stripe charges the cart total regardless of the code, so
          // there is no amount. We render the requested code with an
          // explicit "not applied" note so a reviewer reads it as the
          // agent's intent, not an applied discount.
          <div
            data-testid="settlement-requested-coupon"
            style={{ paddingTop: 2 }}
          >
            <div className="flex items-baseline justify-between">
              <dt
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                }}
              >
                Coupon requested
              </dt>
              <dd
                style={{
                  fontFamily: monoFont,
                  fontSize: 12,
                  color: C.text,
                  fontWeight: 500,
                  textAlign: 'right',
                }}
              >
                {requested_code}
              </dd>
            </div>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                marginTop: 1,
              }}
            >
              not applied · Stripe charges the cart total
            </div>
          </div>
        ) : null}
        {has_tax ? (
          <SettlementRow
            C={C}
            label="Tax"
            amount={formatMinor(tax as number, currency)}
          />
        ) : null}
        {has_shipping ? (
          <SettlementRow
            C={C}
            label="Shipping"
            amount={formatMinor(shipping as number, currency)}
          />
        ) : null}
        {has_total ? (
          <SettlementRow
            C={C}
            label="Total"
            amount={formatMinor(total as number, currency)}
            emphasized
          />
        ) : null}
        {has_refund ? (
          // ADR-0082 §5 — refunded amount, rendered as a negative (money
          // returned to the buyer) in coral, mirroring the Discount row's
          // negative convention.
          <SettlementRow
            C={C}
            label="Refunded"
            amount={`-${formatMinor(refunded as number, currency)}`}
            tone="coral"
          />
        ) : null}
        {net_minor !== null ? (
          // Net settled after the refund (total − refunded). $0.00 on a full
          // refund. Muted — it's a derived reference, not a charged line.
          <SettlementRow
            C={C}
            label="Net"
            amount={formatMinor(net_minor, currency)}
            muted
          />
        ) : null}
        {disputed_amount !== null ? (
          // ADR-0082 §5 — disputed (charged-back) amount. Coral: a dispute is
          // an adverse post-settlement event the merchant must respond to.
          <SettlementRow
            C={C}
            label="Disputed"
            amount={formatMinor(disputed_amount, currency)}
            tone="coral"
          />
        ) : null}
        {has_ceiling ? (
          <SettlementRow
            C={C}
            label="Authorized max"
            amount={formatMinor(ceiling as number, currency)}
            muted
            {...(over_ceiling ? { tone: 'coral' as const } : {})}
          />
        ) : null}
      </dl>
      {/* P1 — the Shopify order reference (already on the wire). Shows
          the numeric order id so a reviewer can find it in Shopify
          Admin; the gid's trailing segment is the admin order number. */}
      {typeof settlement.shopify_order_id === 'string'
        && settlement.shopify_order_id.length > 0 ? (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: `1px solid ${C.borderSoft}`,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
          }}
        >
          Shopify order ·{' '}
          <span style={{ color: C.text }}>
            {settlement.shopify_order_id.split('/').pop()}
          </span>
        </div>
      ) : null}
      {/* Stripe payment reference — mirrors the Shopify order row above.
          Prefers the PaymentIntent id (`pi_…`); falls back to the
          charge id (`ch_…`) when the PI id is absent (legacy
          reconciliation rows). Lets a reviewer find the payment in the
          Stripe dashboard. */}
      {stripe_payment_reference !== null ? (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: `1px solid ${C.borderSoft}`,
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
          }}
        >
          Stripe payment ·{' '}
          <span style={{ color: C.text }}>{stripe_payment_reference}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Friendly-case a rail `failure_reason` enum value for display, e.g.
 * `card_declined` → `Card declined`. The reason is a bounded
 * `ConnectorErrorClass` from the server's allow-list; this is a pure
 * presentation transform (snake_case → sentence case).
 */
function friendlyFailureReason(reason: string): string {
  const spaced = reason.replace(/_/g, ' ').trim();
  if (spaced.length === 0) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * ADR-0151 — humanize a pre-dispatch settlement-abort reason (an abort
 * written by a rail-neutral consumer gate BEFORE `railSelector.select()`
 * runs). Distinct from `friendlyFailureReason` (the rail-decline path):
 * a pre-dispatch abort is a Sill-policy block, not a rail decline, so
 * the operator needs a full sentence explaining WHY the gate fired and
 * that no charge was made.
 *
 * Extensible: a new rule (r32, r33, …) adds one entry. UNKNOWN reasons
 * NEVER crash — the fallback is the underscore-friendly cased form
 * (same shape as `friendlyFailureReason`), so a forward-compat wire
 * value still reads cleanly. NEVER emits raw `snake_case_enum` text
 * when a friendly form is available.
 */
export function humanizeSettlementAbortReason(reason: string): string {
  switch (reason) {
    case 'cross_agent_org_budget_exceeded':
      return (
        'Cross-agent organization budget exceeded (r31) — '
        + "the organization's rolling spend cap across all agents was "
        + 'reached, so no charge was made.'
      );
    default:
      // Unknown / forward-compat reason. Fall back to the sentence-case
      // friendly transform so the panel never surfaces a raw
      // `snake_case_enum` and never crashes on a value we haven't
      // added a full sentence for.
      return friendlyFailureReason(reason);
  }
}

interface SettlementRowProps {
  C: ReturnType<typeof useC>;
  label: string;
  amount: string;
  /** Bolder type + top border separator above the row. Used for `Total`. */
  emphasized?: boolean;
  /** Muted color on both label + amount. Used for `Authorized max`. */
  muted?: boolean;
  /** Optional accent tone on the amount (e.g. coral when over ceiling). */
  tone?: 'coral';
}

function SettlementRow({
  C,
  label,
  amount,
  emphasized,
  muted,
  tone,
}: SettlementRowProps): JSX.Element {
  const amount_color =
    tone === 'coral' ? C.coralFg : muted ? C.muted : C.text;
  return (
    <div
      className="flex items-baseline justify-between"
      style={{
        paddingTop: emphasized ? 6 : 2,
        marginTop: emphasized ? 4 : 0,
        borderTop: emphasized ? `1px solid ${C.border}` : undefined,
      }}
    >
      <dt
        style={{
          fontFamily: monoFont,
          fontSize: emphasized ? 10 : 9,
          color: muted ? C.muted : C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          fontFamily: monoFont,
          fontSize: emphasized ? 14 : 12,
          color: amount_color,
          fontWeight: emphasized ? 700 : 500,
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'right',
        }}
      >
        {amount}
      </dd>
    </div>
  );
}

export function Anchor({
  C,
  label,
  value,
}: {
  C: { muted: string; text: string };
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="mb-2 last:mb-0">
      <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1.5 }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.text,
          wordBreak: 'break-all',
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * ADR-0059 amendment §A.4.3 — rolled-up advisory verdict pill placed
 * above the rule trace. Maps the per-rule outcomes to one of four
 * states + tones; the kicker text ALWAYS carries the `ADVISORY` suffix
 * so the merchant cannot mistake "would have rejected" for "was
 * blocked".
 */
export function AdvisoryVerdictPill({
  C,
  rules,
}: {
  C: ReturnType<typeof useC>;
  rules: RuleResult[];
}): JSX.Element {
  const verdict = rollupVerdict(rules);
  const { tone, label } = (() => {
    switch (verdict) {
      case 'pass':
        return { tone: 'lime' as PillTone, label: 'MATCHES POLICY · ADVISORY' };
      case 'reject':
        return { tone: 'coral' as PillTone, label: 'WOULD HAVE REJECTED · ADVISORY' };
      case 'escalate':
        return { tone: 'amber' as PillTone, label: 'WOULD HAVE ESCALATED · ADVISORY' };
      case 'none':
      default:
        return { tone: 'neutral' as PillTone, label: 'NO POLICY MATCH · ADVISORY' };
    }
  })();
  return (
    <div className="mb-3 flex items-center gap-2">
      <Pill tone={tone}>{label}</Pill>
      <span
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.4,
        }}
      >
        Observe-only; the call still executed.
      </span>
    </div>
  );
}

/**
 * Helper — collapse a `RuleResult[]` into a single rolled-up verdict
 * for the advisory pill. Mirrors `intent-label.ts` patterns; could be
 * lifted there if more views need it.
 */
export function rollupVerdict(
  rules: RuleResult[],
): 'pass' | 'reject' | 'escalate' | 'none' {
  if (rules.length === 0) return 'none';
  // Prioritize reject → escalate → pass. If every rule passed it's pass.
  let saw_pass = false;
  for (const r of rules) {
    if (r.action_taken === 'reject' || r.action_taken === 'evaluator_error') {
      return 'reject';
    }
  }
  for (const r of rules) {
    if (r.action_taken === 'escalate') return 'escalate';
  }
  for (const r of rules) {
    if (r.action_taken === 'allow' || r.action_taken === 'allow_exemption') {
      saw_pass = true;
    }
  }
  return saw_pass ? 'pass' : 'none';
}

/**
 * ADR-0059 §6 — Request + Response inspection panel. Renders the
 * decrypted canonical-JSON content from the
 * `audit_record_payload` row, plus a "Verify hash" affordance that
 * re-canonicalises the content client-side, computes SHA-256 via
 * Web Crypto, and compares to the signed `request_hash` /
 * `response_hash` on the parent audit row.
 *
 * Hash mismatch is the intended signal that the content was purged
 * after-the-fact OR (rarely) tampered. The dashboard surfaces both
 * cases with a clear UI cue.
 */
export function PayloadPanel({
  C,
  payload,
  request_hash,
  response_hash,
}: {
  C: ReturnType<typeof useC>;
  payload: AuditRecordPayload;
  request_hash: string;
  response_hash: string;
}): JSX.Element {
  const timeZone = useDisplayTimeZone();
  const purged = payload.purged_at !== undefined;
  return (
    <div
      className="p-3 mb-5"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 8,
        }}
      >
        REQUEST / RESPONSE CAPTURE
      </div>
      {purged ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.5,
            padding: 8,
            background: 'transparent',
            border: `1px dashed ${C.borderSoft}`,
            borderRadius: 2,
          }}
        >
          Content purged at {payload.purged_at !== undefined ? formatIsoDate(payload.purged_at, timeZone) : 'an unknown time'}{' '}
          {payload.purged_by !== undefined ? `(${payload.purged_by})` : ''}.
          The signed envelope on this record remains valid; the
          inspectable bytes have been withheld.
        </div>
      ) : payload.decrypt_failed === true ? (
        /* ADR-0059 security review MAJOR M4 — integrity-class
         * decrypt failure (auth_tag_invalid / aad_mismatch). Renders
         * loudly so the merchant cannot mistake it for "no content
         * captured". */
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.coralFg,
            lineHeight: 1.5,
            padding: 8,
            background: 'transparent',
            border: `1px solid ${C.coralFg}`,
            borderRadius: 2,
          }}
        >
          Content failed integrity check. The stored ciphertext for
          this record did not verify under its AEAD tag — this signals
          tampering or a cross-tenant injection attempt and has been
          reported to ops. The signed envelope itself remains valid.
        </div>
      ) : (
        <>
          <PayloadSide
            C={C}
            label="Request"
            value={payload.request}
            expected_hash={request_hash}
          />
          <div style={{ height: 12 }} />
          <PayloadSide
            C={C}
            label="Response"
            value={payload.response}
            expected_hash={response_hash}
          />
        </>
      )}
    </div>
  );
}

function PayloadSide({
  C,
  label,
  value,
  expected_hash,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  value: AuditRecordPayload['request'];
  expected_hash: string;
}): JSX.Element {
  const [verify_state, setVerifyState] = useState<
    'idle' | 'verifying' | 'matched' | 'mismatch'
  >('idle');
  // Reset verification state when the content changes.
  useEffect(() => {
    setVerifyState('idle');
  }, [value, expected_hash]);
  const onVerify = async (): Promise<void> => {
    if (value === undefined) return;
    setVerifyState('verifying');
    try {
      const matched = await verifyContentHash(value.canonical_json, expected_hash);
      setVerifyState(matched ? 'matched' : 'mismatch');
    } catch {
      setVerifyState('mismatch');
    }
  };

  if (value === undefined) {
    return (
      <div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 1.5,
            marginBottom: 4,
          }}
        >
          {label.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            fontStyle: 'italic',
          }}
        >
          Content unavailable.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 1.5,
          }}
        >
          {label.toUpperCase()} · {value.byte_length}B{value.truncated ? ' · TRUNCATED' : ''}
        </div>
        <button
          type="button"
          onClick={() => void onVerify()}
          disabled={verify_state === 'verifying'}
          className="flex items-center gap-1 px-2 py-0.5"
          style={{
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1,
            borderRadius: 2,
            cursor: verify_state === 'verifying' ? 'not-allowed' : 'pointer',
          }}
        >
          <ShieldCheck size={11} />
          {verify_state === 'verifying' ? 'VERIFYING…' : 'VERIFY HASH'}
        </button>
        {verify_state === 'matched' ? (
          <span
            className="flex items-center gap-1"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.limeFg,
              letterSpacing: 1,
            }}
          >
            <Check size={11} /> HASH MATCHES
          </span>
        ) : null}
        {verify_state === 'mismatch' ? (
          <span
            className="flex items-center gap-1"
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.coralFg,
              letterSpacing: 1,
            }}
          >
            <X size={11} /> HASH MISMATCH
          </span>
        ) : null}
      </div>
      <pre
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.text,
          background: 'transparent',
          border: `1px solid ${C.borderSoft}`,
          borderRadius: 2,
          padding: 8,
          margin: 0,
          maxHeight: 240,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {safeJsonStringify(value.canonical_json)}
      </pre>
    </div>
  );
}

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Re-canonicalise + Web-Crypto SHA-256 + base64url-encode + compare to
 * the expected hash. Uses a JCS-equivalent canonicaliser (sort keys,
 * stable number formatting) so the bytes the dashboard hashes are the
 * same bytes the server canonicalised at write time.
 *
 * NOTE: this re-canonicaliser is a JCS-light implementation; the
 * server uses `@truestamp/canonify` (full RFC 8785). For Slice 1 the
 * shapes captured (browse_catalog args, place_order responses) are
 * flat objects + arrays of primitives; the simplified canonicaliser
 * matches. A future slice can adopt a shared JCS library between
 * server and dashboard.
 */
async function verifyContentHash(
  canonical_value: unknown,
  expected_hash: string,
): Promise<boolean> {
  const canonical_str = jcsLight(canonical_value);
  const enc = new TextEncoder().encode(canonical_str);
  const digest = await crypto.subtle.digest('SHA-256', enc);
  const b64 = bytesToBase64Url(new Uint8Array(digest));
  return b64 === expected_hash;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  const standard = btoa(binary);
  return standard.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * JCS-light canonicaliser — sort object keys lexicographically; drop
 * undefined values; render numbers/strings/bools via JSON.stringify.
 */
function jcsLight(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return JSON.stringify(value);
  if (typeof value === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map((v) => jcsLight(v)).join(',') + ']';
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>).sort();
    const parts: string[] = [];
    for (const k of keys) {
      const v = (value as Record<string, unknown>)[k];
      if (v === undefined) continue;
      parts.push(JSON.stringify(k) + ':' + jcsLight(v));
    }
    return '{' + parts.join(',') + '}';
  }
  return 'null';
}
